// OCR core — orchestrates UI, queue, the recognition engine, results workspace.
// Depends on: OcrEngine (+PPOCR) or Tesseract, OcrPre, OcrPdf, OcrExport, OcrCamera, JSZip, docx, XLSX, jsPDF, jsQR.
//
// Engines (2026-09-12). Recognition goes through window.OcrEngine — the neural
// PP-OCRv5 lane for the eight scripts it knows (every locale this site ships),
// measured on tests/ocr against the Tesseract pipeline THIS file ran until
// today: 37 % → 2.4 % CER on the synthetic corpus in production configuration
// (Sauvola erased dark screenshots and inverted signs entirely), and on real
// documents the same or better on scans and far better on phone photos. A
// language outside those scripts, or a page where the neural lane cannot
// start, keeps the Tesseract pool below byte for byte.
(function () {
  'use strict';

  // ─── Language catalogue ────────────────────────────────────────────────
  const LANGS = [
    ['popular', [
      ['eng', 'English'], ['rus', 'Русский'], ['chi_sim', '中文 (简体)'],
      ['spa', 'Español'], ['fra', 'Français'], ['deu', 'Deutsch'],
      ['jpn', '日本語'], ['kor', '한국어'], ['ara', 'العربية'],
      ['hin', 'हिन्दी'], ['por', 'Português'], ['ita', 'Italiano'],
      ['tur', 'Türkçe'], ['pol', 'Polski'], ['nld', 'Nederlands'],
      ['vie', 'Tiếng Việt'], ['ind', 'Bahasa Indonesia'], ['tha', 'ภาษาไทย'],
    ]],
    ['europe', [
      ['ces', 'Čeština'], ['slk', 'Slovenčina'], ['hun', 'Magyar'],
      ['ron', 'Română'], ['swe', 'Svenska'], ['dan', 'Dansk'],
      ['nor', 'Norsk'], ['fin', 'Suomi'], ['ell', 'Ελληνικά'],
      ['bul', 'Български'], ['hrv', 'Hrvatski'], ['srp', 'Српски'],
      ['slv', 'Slovenščina'], ['cat', 'Català'], ['est', 'Eesti'],
      ['lav', 'Latviešu'], ['lit', 'Lietuvių'], ['isl', 'Íslenska'],
      ['mlt', 'Malti'], ['eus', 'Euskara'], ['glg', 'Galego'], ['gle', 'Gaeilge'],
      ['cym', 'Cymraeg'], ['epo', 'Esperanto'], ['lat', 'Latina'],
    ]],
    ['cjk', [
      ['chi_tra', '中文 (繁體)'], ['chi_sim_vert', '中文 (竖排)'], ['jpn_vert', '日本語 (縦書き)'],
    ]],
    ['indic', [
      ['ben', 'বাংলা'], ['guj', 'ગુજરાતી'], ['kan', 'ಕನ್ನಡ'], ['mal', 'മലയാളം'],
      ['mar', 'मराठी'], ['nep', 'नेपाली'], ['ori', 'ଓଡ଼ିଆ'], ['pan', 'ਪੰਜਾਬੀ'],
      ['san', 'संस्कृतम्'], ['sin', 'සිංහල'], ['tam', 'தமிழ்'], ['tel', 'తెలుగు'], ['urd', 'اردو'],
    ]],
    ['arabic', [
      ['heb', 'עברית'], ['fas', 'فارسی'], ['pus', 'پښتو'], ['uig', 'ئۇيغۇرچە'], ['yid', 'ייִדיש'],
    ]],
    ['cyrillic', [
      ['ukr', 'Українська'], ['bel', 'Беларуская'], ['kaz', 'Қазақша'],
      ['aze', 'Azərbaycan'], ['aze_cyrl', 'Азәрбајҹан (кир.)'],
      ['tgk', 'Тоҷикӣ'], ['mkd', 'Македонски'], ['mon', 'Монгол'],
    ]],
    ['other', [
      ['afr', 'Afrikaans'], ['amh', 'አማርኛ'], ['asm', 'অসমীয়া'], ['bod', 'བོད་ཡིག'],
      ['bos', 'Bosanski'], ['chr', 'ᏣᎳᎩ'], ['div', 'ދިވެހި'], ['dzo', 'རྫོང་ཁ'],
      ['enm', 'Middle English'], ['frm', 'Français moyen'], ['frk', 'Fraktur'],
      ['kat', 'ქართული'], ['khm', 'ខ្មែរ'], ['kir', 'Кыргызча'], ['lao', 'ລາວ'],
      ['ltz', 'Lëtzebuergesch'], ['mri', 'Māori'], ['msa', 'Melayu'], ['mya', 'မြန်မာ'],
      ['oci', 'Occitan'], ['que', 'Runasimi'], ['snd', 'سنڌي'], ['spa_old', 'Español antiguo'],
      ['sqi', 'Shqip'], ['swa', 'Kiswahili'], ['syr', 'ܣܘܪܝܝܐ'], ['tat', 'Татар'],
      ['tir', 'ትግርኛ'], ['tgl', 'Tagalog'], ['uzb', 'Oʻzbek'], ['uzb_cyrl', 'Ўзбек (кир.)'],
      ['yor', 'Yorùbá'],
    ]],
  ];

  const LANG_LOOKUP = {};
  LANGS.forEach(([, arr]) => arr.forEach(([code, name]) => { LANG_LOOKUP[code] = name; }));

  // langPath/workerPath/corePath: pass only when we have an explicit override
  // (e.g. RU users behind VPS proxy). null → Tesseract.js picks its bundled default.
  const TESSDATA_CDN = window.__OCR_LANGPATH || null;
  const WORKER_PATH = window.__OCR_WORKERPATH || null;
  const CORE_PATH = window.__OCR_COREPATH || null;
  const MAX_LANGS = 3;
  const WORKER_POOL_SIZE = Math.max(1, Math.min(4, (navigator.hardwareConcurrency || 2) - 1));
  const MAX_PARALLEL = 50;
  // Big-PDF guard: past this page count the queue row grows a range picker and
  // only the selected span is rasterized (memory stays bounded; the reporter's
  // Neufert scan was 600+ pages — rendering it whole would OOM the tab).
  const PDF_RANGE_THRESHOLD = 150;
  const PDF_RANGE_MAX_SPAN = 150;
  // Daily cap (config/tiers.php, Farid 20.10.2026): the unit is a FILE — the same
  // file again with another language or output style is a free repeat; another
  // page range of a big PDF is another unit (limitKeyOf). The plan also caps
  // the pages one pass may take (max_pages → tierLimits.tool_max_pages).
  const LIMIT_SLUG = 'ocr-text-extractor';

  // ─── Telemetry ─────────────────────────────────────────────────────────
  function ae(type, payload) {
    try { if (window.__ae) window.__ae(type, payload); } catch (e) {}
  }
  // Shared conversion funnel (same pattern as redact-pdf): tool_funnel rows for
  // the per-stage dashboard + window.__stage() so the site-wide abandon tracker
  // (pagehide → tool_stage_final) can attribute where users give up. 'export'
  // maps to 'complete' — a finished export must never be counted as an abandon.
  // 'result' — the text is on screen. Until 14.10.2026 a run that FINISHED but
  // was read on screen (or copied with the editor button) left its session at
  // 'processing', so «abandoned while processing» counted the people who got
  // their text along with the ones whose engine hung; the stage now says which.
  const _funnelSeen = {};
  const STAGE_MAP = { upload: 'upload', processing: 'processing', result: 'result', export: 'complete' };
  function funnel(stage, payload) {
    if (_funnelSeen[stage]) return;
    _funnelSeen[stage] = 1;
    try { if (window.__ae) window.__ae('tool_funnel', Object.assign({ slug: 'ocr-text-extractor', stage }, payload || {})); } catch (e) {}
    try { if (window.__stage && STAGE_MAP[stage]) window.__stage(STAGE_MAP[stage], { tool: 'ocr-text-extractor' }); } catch (e) {}
  }

  // ─── App state ─────────────────────────────────────────────────────────
  const SETTINGS_KEY = 'ocr-text-extractor';
  function loadSetting(key, fallback) {
    try {
      if (window.__toolSettings) return window.__toolSettings.load(SETTINGS_KEY, key, fallback);
      const raw = localStorage.getItem(SETTINGS_KEY + ':' + key);
      return raw == null ? fallback : JSON.parse(raw);
    } catch (e) { return fallback; }
  }
  function saveSetting(key, value) {
    try {
      if (window.__toolSettings) window.__toolSettings.save(SETTINGS_KEY, key, value);
      else localStorage.setItem(SETTINGS_KEY + ':' + key, JSON.stringify(value));
    } catch (e) {}
  }

  const state = {
    queue: [],              // [{id, file, name, size, thumbUrl, status, result}]
    selectedLangs: loadSetting('langs', ['auto']),
    mode: loadSetting('mode', 'plain'),
    preprocess: loadSetting('preprocess', { auto: true }),
    activeResult: null,
    activePageIdx: 0,
    scheduler: null,
    engine: null,           // OcrEngine handle (neural lane) — null while the Tesseract pool serves
    engineKey: null,
    workers: null,          // direct worker refs for setParameters (PSM per mode)
    appliedPsm: null,
    probeWorker: null,      // dedicated sparse worker for the orientation probe
    probeWorkerKey: null,
    isProcessing: false,
    cancelled: false,
    loadedLangs: new Set(),
    qrScanDone: false,      // legacy (QR is now latched per file item)
  };

  const T = () => (window.__OCR_I18N || {});
  const $ = sel => document.querySelector(sel);
  const $$ = sel => Array.from(document.querySelectorAll(sel));

  function toast(msg, type) {
    if (window.__toast) window.__toast(msg, type || 'info');
    else console.log('[OCR]', msg);
  }

  function handleError(err, ctx) {
    console.error('[OCR]', ctx || '', err);
    if (window.__handleError) window.__handleError(err, ctx);
  }
  // A rejection is not always an Error: a string from a worker, an Event, or
  // nothing at all (Sentry JZ: `undefined is not an object (evaluating
  // 'u.message')` in the batch catch itself). Every catch below reads through
  // this, so a bad engine day ends in the user's toast, not in Sentry.
  function asError(e) {
    if (e instanceof Error) return e;
    if (e == null) return new Error('unknown error');
    if (typeof e === 'string') return new Error(e);
    const err = new Error(e.message || e.type || String(e));
    if (e.code) err.code = e.code;
    return err;
  }

  // ─── Resume session (IndexedDB) ─────────────────────────────────────────
  // Stores queue files + extracted results so a closed/refreshed tab can
  // restore work-in-progress on next visit. 7-day TTL, single slot per device.
  const RESUME_DB = 'ocr-resume';
  const RESUME_STORE = 'sessions';
  const RESUME_KEY = 'current';
  const RESUME_TTL_MS = 7 * 24 * 60 * 60 * 1000;

  function openResumeDb() {
    return new Promise((resolve, reject) => {
      if (!window.indexedDB) return reject(new Error('no-idb'));
      const req = indexedDB.open(RESUME_DB, 1);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(RESUME_STORE)) db.createObjectStore(RESUME_STORE);
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error || new Error('idb-open-failed'));
    });
  }

  let saveSessionTimer = null;
  function saveSession() {
    if (saveSessionTimer) return;
    saveSessionTimer = setTimeout(() => {
      saveSessionTimer = null;
      saveSessionNow().catch(() => {});
    }, 600);
  }

  async function saveSessionNow() {
    if (!state.queue.length) return clearSession();
    try {
      const db = await openResumeDb();
      const items = await Promise.all(state.queue.map(async q => ({
        id: q.id,
        name: q.name,
        size: q.size,
        thumbUrl: q.thumbUrl,
        status: q.status === 'processing' ? 'pending' : q.status,
        source: q.source || 'drop',
        limitKey: q.limitKey || null,
        pdfPageCount: q.pdfPageCount || 0,
        range: q.range || null,
        sig: q.sig || null,
        doneRange: q.doneRange != null ? q.doneRange : null,
        preciseDone: !!q.preciseDone,
        preciseCharged: q.preciseCharged || 0,
        // Files re-saved as ArrayBuffer; thumbnails via dataURL (already strings)
        bytes: q.file ? await q.file.arrayBuffer().catch(() => null) : null,
        type: q.file ? q.file.type : '',
        result: q.result ? {
          fileName: q.result.fileName,
          text: q.result.text,
          words: q.result.words,
          confidence: q.result.confidence,
          language: q.result.language,
          pageCount: q.result.pageCount,
          parsed: q.result.parsed || null,
          engine: q.result.engine || null,
          provider: q.result.provider || null,
          // pages with imageDataUrl; strings, no blobs
          pages: (q.result.pages || []).map(p => ({
            pageNumber: p.pageNumber,
            totalPages: p.totalPages,
            imageDataUrl: p.imageDataUrl,
            imageWidth: p.imageWidth,
            imageHeight: p.imageHeight,
            text: p.text,
            words: p.words,
            confidence: p.confidence,
            language: p.language,
            precise: !!p.precise,
          })),
        } : null,
      })));
      const tx = db.transaction(RESUME_STORE, 'readwrite');
      tx.objectStore(RESUME_STORE).put({ savedAt: Date.now(), items }, RESUME_KEY);
      tx.oncomplete = () => db.close();
    } catch (e) { /* idb full / blocked / private — fail silent */ }
  }

  async function clearSession() {
    try {
      const db = await openResumeDb();
      const tx = db.transaction(RESUME_STORE, 'readwrite');
      tx.objectStore(RESUME_STORE).delete(RESUME_KEY);
      tx.oncomplete = () => db.close();
    } catch (e) {}
  }

  async function loadSession() {
    try {
      const db = await openResumeDb();
      return await new Promise((resolve, reject) => {
        const tx = db.transaction(RESUME_STORE, 'readonly');
        const req = tx.objectStore(RESUME_STORE).get(RESUME_KEY);
        req.onsuccess = () => { db.close(); resolve(req.result || null); };
        req.onerror = () => { db.close(); reject(req.error); };
      });
    } catch (e) { return null; }
  }

  async function offerResume() {
    const sess = await loadSession();
    if (!sess || !sess.items || !sess.items.length) return;
    if (Date.now() - sess.savedAt > RESUME_TTL_MS) { clearSession(); return; }

    const banner = document.createElement('div');
    banner.className = 'ocr-glass ocr-resume-banner';
    banner.innerHTML =
      '<span class="ocr-resume-text">' +
      ((T().resume_prompt || 'Restore your previous work — {n} file|Restore your previous work — {n} files')
        .replace(/{n}/g, sess.items.length)
        .split('|')[sess.items.length === 1 ? 0 : 1]) +
      '</span>' +
      '<button type="button" class="ocr-action-btn primary" data-resume-go>' + (T().resume_yes || 'Restore') + '</button>' +
      '<button type="button" class="ocr-action-btn ghost" data-resume-skip>' + (T().resume_no || 'Discard') + '</button>';
    const app = $('.ocr-app');
    if (app) app.insertBefore(banner, app.firstChild);

    banner.querySelector('[data-resume-skip]').onclick = () => {
      banner.remove();
      clearSession();
      ae('feature_use', { slug: 'ocr-text-extractor', action: 'resume_discarded' });
    };
    banner.querySelector('[data-resume-go]').onclick = async () => {
      banner.remove();
      ae('feature_use', { slug: 'ocr-text-extractor', action: 'resume_restored', files: sess.items.length });
      for (const it of sess.items) {
        if (!it.bytes) continue;
        const file = new File([it.bytes], it.name, { type: it.type || 'application/octet-stream' });
        state.queue.push({
          id: it.id,
          file,
          name: it.name,
          size: it.size,
          thumbUrl: it.thumbUrl,
          status: it.result ? 'done' : 'pending',
          result: it.result,
          source: it.source || 'resume',
          // A session saved before 20.10.2026 carries no key/settings: the
          // rebuilt File has a new lastModified, so the saved key is the only
          // one that keeps a re-read of this file free today.
          limitKey: it.limitKey || ((window.__toolLimit && window.__toolLimit.fileKey) ? window.__toolLimit.fileKey(file) : null),
          pdfPageCount: it.pdfPageCount || 0,
          range: it.range || null,
          sig: it.result ? (it.sig || runSig()) : null,
          preciseDone: !!it.preciseDone,
          preciseCharged: it.preciseCharged || 0,
          doneRange: it.doneRange != null ? it.doneRange : (it.range ? it.range.from + '-' + it.range.to : ''),
        });
      }
      queueChanged();
      const firstDone = state.queue.find(q => q.result);
      if (firstDone) showResult(firstDone);
    };
  }

  function plural(template, n) {
    // Laravel pluralization: "x|y" or "x|y|z". Fallback: first form.
    if (!template) return '' + n;
    const parts = template.split('|');
    const out = parts.length === 1 ? parts[0]
      : parts.length === 2 ? (n === 1 ? parts[0] : parts[1])
      : (() => {
        const mod10 = n % 10, mod100 = n % 100;
        if (mod10 === 1 && mod100 !== 11) return parts[0];
        if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return parts[1];
        return parts[2];
      })();
    return out.replace('{n}', n);
  }

  // ─── Initial wiring ────────────────────────────────────────────────────
  function init() {
    bindUpload();
    bindCamera();
    bindLanguage();
    bindMode();
    bindPreprocess();
    bindSubmit();
    bindWorkspaceTabs();
    bindBbox();
    bindSearch();
    bindText();
    bindExport();
    bindReadAloud();
    bindFindReplace();
    bindPrint();
    bindShortcutsModal();
    bindWorkspaceZoom();
    bindQueueDragReorder();
    bindPrecise();
    renderLanguagePanel();
    renderSelectedLangs();
    bindKeyboard();
    offerResume();
    window.addEventListener('beforeunload', () => { saveSessionNow().catch(() => {}); });
    // Restore mode selection from localStorage
    const activeMode = document.querySelector('[data-mode-val="' + state.mode + '"]');
    if (activeMode) {
      $$('[data-mode-val]').forEach(c => c.classList.remove('active'));
      activeMode.classList.add('active');
    }
    // Restore preprocess checkboxes
    $$('[data-pp]').forEach(cb => { cb.checked = !!state.preprocess[cb.dataset.pp]; });
    syncPreprocessPanel();
    syncSubmit();
    // The guest map judged the page ceiling until the account's snapshot came.
    document.addEventListener('tier-bootstrap:ready', () => { if (state.queue.length && !state.isProcessing) queueChanged(); });
    ae('ocr_init', { pool: WORKER_POOL_SIZE, locale: window.__OCR_LOCALE || 'en' });
    funnel('open');
  }

  function bindKeyboard() {
    document.addEventListener('keydown', e => {
      // Ctrl/Cmd+Enter → start OCR (works only when focus is outside editable text)
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        const target = e.target;
        if (target && target.isContentEditable) return;
        if (state.queue.length && !state.isProcessing) {
          e.preventDefault();
          runBatch();
        }
      }
      // Esc → close camera, panels
      if (e.key === 'Escape') {
        const cam = $('[data-camera-ui]');
        if (cam && cam.classList.contains('active')) {
          window.OcrCamera.close();
          cam.classList.remove('active');
          return;
        }
        const langPanel = $('[data-lang-panel]');
        if (langPanel && langPanel.classList.contains('active')) {
          langPanel.classList.remove('active');
          return;
        }
        // Esc cancels a running batch — the shortcuts modal has advertised
        // this since launch; pre-fix only the Cancel button worked.
        if (state.isProcessing && !state.cancelled) {
          state.cancelled = true;
          ae('feature_use', { slug: 'ocr-text-extractor', action: 'cancel_esc' });
          return;
        }
      }
    });
  }

  // ─── Upload handlers ────────────────────────────────────────────────────
  // Drag/drop/click owned by shared <x-tool-uploader> component; we listen
  // to its `tool-uploader:file` event and call addFiles().
  function bindUpload() {
    const zone = $('[data-upload-zone]');
    if (!zone) return;

    // The folded zone's «Другой файл» REPLACES the queue (that is what its words
    // say); «Добавить ещё» in the queue head, a drop, a paste and the camera add.
    let replaceNext = false;
    zone.addEventListener('click', e => {
      if (e.target && e.target.closest && e.target.closest('.tu-picked-change')) replaceNext = true;
    }, true);

    zone.addEventListener('tool-uploader:file', e => {
      const files = (e.detail && e.detail.files) || (e.detail && e.detail.file ? [e.detail.file] : []);
      const source = (e.detail && e.detail.source) || 'drop';
      // Only a pick from the dialog the button opened replaces; a later drop or
      // paste (the dialog was cancelled) adds, as everywhere else.
      if (replaceNext && files.length && !state.isProcessing && (source === 'click' || source === 'keyboard')) clearQueue();
      replaceNext = false;
      if (files.length) addFiles(files, source);
    });
    // × on the folded row: the person took the files away.
    zone.addEventListener('tool-uploader:clear', () => { if (!state.isProcessing) clearQueue(); });

    const addBtn = $('[data-queue-add]');
    if (addBtn) addBtn.addEventListener('click', () => {
      replaceNext = false;
      if (zone.__uploaderOpen) zone.__uploaderOpen();
    });

    const clearBtn = $('[data-queue-clear]');
    if (clearBtn) clearBtn.addEventListener('click', clearQueue);
  }

  function clearQueue() {
    state.queue.forEach(item => { if (item.thumbUrl) URL.revokeObjectURL(item.thumbUrl); });
    state.queue = [];
    state.activeResult = null;
    const ws = $('[data-ws]');
    if (ws) ws.classList.remove('active');
    queueChanged();
    clearSession();
  }

  /* Ctrl+V belongs to the shared uploader: it takes a pasted image page-wide
   * (not inside the editable text) and hands it over as tool-uploader:file with
   * source 'paste'. This tool's own document listener ran beside it, so every
   * paste entered the queue TWICE — 427 of 432 sessions that pasted in the 30
   * days to 21.10.2026, each screenshot read two times. */

  function bindCamera() {
    const openBtn = $('[data-camera]');
    const ui = $('[data-camera-ui]');
    const shoot = $('[data-camera-shoot]');
    const closeBtn = $('[data-camera-close]');
    const switchBtn = $('[data-camera-switch]');
    if (!openBtn || !ui) return;

    openBtn.addEventListener('click', async () => {
      ui.classList.add('active');
      try {
        await window.OcrCamera.open({
          video: $('[data-camera-video]'),
          guide: $('[data-camera-guide]'),
        });
      } catch (err) {
        ui.classList.remove('active');
        // The camera door names the reason (denied / busy / no camera / no frames)
        // in this locale; the name checks below are for a page without it.
        const told = window.OcrCamera.describeError ? window.OcrCamera.describeError(err) : null;
        if (told && told.message) {
          toast(told.message, 'error');
        } else if (err && (err.name === 'NotAllowedError' || err.name === 'NotAllowedError')) {
          toast(T().camera_denied, 'error');
        } else if (err && err.name === 'NotFoundError') {
          toast(T().camera_unavailable, 'error');
        } else {
          toast((err && err.message) || T().camera_denied, 'error');
        }
      }
    });

    if (closeBtn) closeBtn.addEventListener('click', () => {
      window.OcrCamera.close();
      ui.classList.remove('active');
    });

    if (switchBtn) switchBtn.addEventListener('click', async () => {
      try { await window.OcrCamera.switchCamera(); } catch (e) { toast(e.message || 'Error', 'error'); }
    });

    if (shoot) shoot.addEventListener('click', () => {
      const canvas = window.OcrCamera.capture();
      // no frame this instant: the camera stays open and the press is answered
      if (!canvas) { toast(window.__cameraNoFrame(), 'warning'); return; }
      const cropped = window.OcrCamera.autoCropDocument(canvas);
      window.OcrCamera.close();
      ui.classList.remove('active');
      ae('ocr_camera_shot', {
        w: canvas.width, h: canvas.height,
        cropped: cropped !== canvas ? 1 : 0,
      });
      cropped.toBlob(blob => {
        if (!blob) return;
        const file = new File([blob], 'camera-' + Date.now() + '.jpg', { type: 'image/jpeg' });
        addFiles([file], 'camera');
      }, 'image/jpeg', 0.92);
    });
  }

  /* URL-by-link input REMOVED 2026-07-30 (Farid). It could never work from the
   * page: fetch(url,{mode:"cors"}) is subject to our own CSP `connect-src`,
   * which is an allow-list. MEASURED on prod — same-origin ✓, Wikipedia /
   * GitHub raw / placehold.co / arbitrary site all «Failed to fetch». The only
   * link that ever worked pointed back at this site, while the error message
   * blamed the user's URL. A server proxy would lift the restriction but adds
   * SSRF exposure, so the field is gone instead of half-working.
   * Do not re-add without a proxy: the markup alone will not help. */

  // ─── What a run would do ────────────────────────────────────────────────
  // The settings a result was read with. A finished file whose settings have
  // since changed is work again: until 20.10.2026 the button skipped every
  // «done» file, so changing the language after a weak result (which our own
  // hint_wrong_lang tells people to do) and pressing «Extract» did nothing —
  // 331 sessions in 30 days logged that press as silent_click.
  function runSig() {
    const pp = ppMatters() ? Object.keys(state.preprocess).filter(k => state.preprocess[k]).sort().join(',') : '';
    return resolveLangs().join('+') + '|' + state.mode + '|' + pp;
  }
  // Preprocessing feeds only the Tesseract lane: the neural engine reads the
  // pixels untouched (processItem, measured 12.09.2026 — the filters made it
  // worse). So the panel is shown only when Tesseract will read these languages;
  // for the site's own languages its seven switches would change nothing.
  function ppMatters() {
    return !(window.OcrEngine && window.OcrEngine.supports(resolveLangs()));
  }
  function syncPreprocessPanel() {
    const box = $('.ocr-pp');
    if (box) box.hidden = !ppMatters();
  }
  function rangeSig(item) {
    return item.range ? item.range.from + '-' + item.range.to : '';
  }
  function needsRun(item) {
    if (item.status === 'processing') return false;
    // A page read in precise mode was paid for: a later change of language or
    // style must not quietly re-read it in the browser and throw that away.
    if (item.preciseDone) return false;
    if (item.status !== 'done' || !item.result) return true;
    return item.sig !== runSig() || item.doneRange !== rangeSig(item);
  }
  // Billing key: stamped once from the PICKED file (the HEIC→JPEG copy gets a
  // fresh lastModified on every pick); a page range of a big PDF is its own unit.
  function limitKeyOf(item) {
    const base = item.limitKey || item.id;
    return item.range ? base + '#p' + item.range.from + '-' + item.range.to : base;
  }
  // Pages one pass may take: the plan's ceiling under the tab's own memory span.
  // No entry (Premium not sold here, or no snapshot) → the tool's own span.
  function pagesCap() {
    const m = window.__tierLimits && window.__tierLimits.tool_max_pages;
    const n = m ? (m[LIMIT_SLUG] | 0) : 0;
    return n > 0 ? Math.min(n, PDF_RANGE_MAX_SPAN) : PDF_RANGE_MAX_SPAN;
  }
  // Fit a PDF's page range to the ceiling. Returns true when the PLAN (not the
  // tab) is what keeps pages out — that case is told in the limit block.
  function fitRange(item) {
    const n = item.pdfPageCount | 0;
    if (!n) return false;
    const span = pagesCap();
    if (n <= Math.min(PDF_RANGE_THRESHOLD, span)) { item.range = null; return false; }
    const f = Math.max(1, Math.min(item.range ? item.range.from : 1, n));
    let t = Math.max(f, Math.min(item.range ? item.range.to : n, n));
    if (t - f + 1 > span) t = f + span - 1;
    item.range = { from: f, to: t };
    return span < PDF_RANGE_MAX_SPAN;
  }
  // Re-fit every waiting PDF (the tier snapshot may arrive after the pick) and
  // say the plan's page ceiling once in the shared limit block, with its
  // Премиум CTA, while a file in the queue is cut by it.
  function fitRanges() {
    let cut = null;
    state.queue.forEach(q => {
      if (q.status !== 'processing' && q.pdfPageCount && fitRange(q) && !cut) cut = q;
    });
    const TL = window.__toolLimit;
    const tpl = T().pages_cap_note || '';
    if (TL && TL.setDetail) {
      TL.setDetail(LIMIT_SLUG, 'pages', cut && tpl
        ? tpl.replace(':total', cut.pdfPageCount).replace(':cap', pagesCap())
        : '');
    }
    if (cut && !cut.capTold) {
      cut.capTold = true;
      ae('feature_use', { slug: LIMIT_SLUG, action: 'pdf_pages_capped', pages: cut.pdfPageCount, cap: pagesCap() });
    }
  }
  // Ask the cap about the queue as soon as it changes, not at the press.
  function precheckQueue() {
    const TL = window.__toolLimit;
    if (!TL || !TL.precheck) return;
    try {
      const keys = state.queue.filter(needsRun).map(limitKeyOf);
      if (keys.length) TL.precheck(LIMIT_SLUG, keys); else TL.clearNote(LIMIT_SLUG);
    } catch (_) {}
  }
  // The action button says what a press will do, and never does nothing:
  // «Extract text» for new files, «Recognize again» when only finished files
  // with changed settings are waiting, and disabled with the reason under it
  // when every file is read with the current settings.
  function syncSubmit() {
    const btn = $('[data-submit]');
    if (!btn || state.isProcessing) return;
    const todo = state.queue.filter(needsRun);
    const again = todo.length > 0 && todo.every(q => q.status === 'done' && q.result);
    btn.textContent = again ? (T().submit_again || 'Recognize again') : (T().submit || 'Extract text');
    btn.disabled = state.queue.length > 0 && !todo.length;
    const hint = $('[data-submit-hint]');
    if (hint) hint.hidden = !btn.disabled;
  }
  // Every change of the queue or of a setting goes through here.
  function queueChanged() {
    fitRanges();
    renderQueue();
    updateSections();
    precheckQueue();
    syncSubmit();
    syncZone();
  }
  // The zone folds into the shared «picked» row while the queue holds files
  // and opens again when the queue is empty (uploader-loaded-state primitive).
  function syncZone() {
    const zone = $('[data-upload-zone]');
    if (!zone) return;
    if (state.queue.length) { if (zone.__uploaderCollapse) zone.__uploaderCollapse(state.queue.map(q => q.file)); }
    else if (zone.classList.contains('tu-collapsed') && zone.__uploaderRestore) zone.__uploaderRestore(true);
  }

  // ─── File intake ────────────────────────────────────────────────────────
  async function addFiles(list, source) {
    source = source || 'drop';
    if (state.queue.length + list.length > MAX_PARALLEL) {
      toast('Maximum ' + MAX_PARALLEL + ' files in the queue', 'warning');
    }
    const slice = Array.from(list).slice(0, MAX_PARALLEL - state.queue.length);
    let added = 0;
    for (const file of slice) {
      if (!isSupported(file)) {
        toast(T().unsupported_format + ': ' + (file.name || ''), 'warning');
        continue;
      }
      if (file.size > 50 * 1024 * 1024) {
        toast(T().file_too_large_warn.replace('{size}', (file.size / 1048576).toFixed(1)), 'info');
      }
      let workingFile = file;
      if (/\.heic$|\.heif$|image\/heic|image\/heif/i.test(file.type + file.name)) {
        try {
          if (window.HeicDecode) {
            const converted = await window.HeicDecode.toBlob(file, 'image/jpeg', 0.92);
            workingFile = new File([converted], file.name.replace(/\.hei[cf]$/i, '.jpg'), { type: 'image/jpeg' });
          }
        } catch (e) { handleError(e, 'heic-decode'); }
      }
      const id = 'f' + Date.now() + Math.random().toString(36).slice(2, 6);
      const thumbUrl = await makeThumbnail(workingFile);
      const limitKey = (window.__toolLimit && window.__toolLimit.fileKey) ? window.__toolLimit.fileKey(file) : null;
      const qItem = { id, file: workingFile, name: workingFile.name, size: workingFile.size, thumbUrl, status: 'pending', result: null, source, limitKey };
      // Big-PDF detection: read the page count cheaply so the queue row can
      // offer a range picker before we commit to rasterizing everything.
      if (await window.OcrPdf.isPdfFile(workingFile)) {
        try {
          const n = await window.OcrPdf.getPageCount(workingFile);
          qItem.pdfPageCount = n;
          // The plan's ceiling is told in the limit block (fitRanges); the tab's
          // own memory span keeps its toast.
          if (!fitRange(qItem) && qItem.range) {
            toast((T().range_hint || 'Large PDF ({n} pages) — pick the page range to OCR.').replace('{n}', n), 'info');
            ae('feature_use', { slug: 'ocr-text-extractor', action: 'pdf_range_offered', pages: n });
          }
        } catch (e) { /* page count is best-effort; render will surface real errors */ }
      }
      state.queue.push(qItem);
      added++;
    }
    if (added > 0) {
      ae('feature_use', { slug: 'ocr-text-extractor', action: 'file_added', source, count: added });
      funnel('upload', { source, count: added });
    }
    queueChanged();
    saveSession();
  }

  function isSupported(file) {
    if (!file) return false;
    const n = (file.name || '').toLowerCase();
    if (/\.(jpe?g|png|webp|tiff?|bmp|heic|heif|pdf)$/i.test(n)) return true;
    return (file.type || '').startsWith('image/') || file.type === 'application/pdf';
  }

  async function makeThumbnail(file) {
    if (file.type === 'application/pdf' || /\.pdf$/i.test(file.name)) return null;
    try {
      const src = await loadImageWithOrientation(file);
      const size = 104;
      const canvas = document.createElement('canvas');
      canvas.width = size; canvas.height = size;
      const ctx = canvas.getContext('2d');
      const s = Math.min(src.width, src.height);
      ctx.drawImage(src, (src.width - s) / 2, (src.height - s) / 2, s, s, 0, 0, size, size);
      return canvas.toDataURL('image/jpeg', 0.8);
    } catch (e) { return null; }
  }

  function loadImage(src) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });
  }

  // Load a file (JPEG/PNG/HEIC/etc.) into a canvas with EXIF orientation
  // already applied. Phone shots commonly arrive with Orientation=6 (90° CW)
  // and a naive drawImage would feed Tesseract a sideways image.
  async function loadImageWithOrientation(file) {
    const isHeic = /heic|heif/i.test(file.type || '') || /\.heic$|\.heif$/i.test(file.name || '');
    if (isHeic && window.HeicDecode) {
      try {
        const out = await window.HeicDecode.toBlob(file, 'image/png');
        const blob = Array.isArray(out) ? out[0] : out;
        const url = URL.createObjectURL(blob);
        try {
          const img = await loadImage(url);
          const c = document.createElement('canvas');
          c.width = img.width; c.height = img.height;
          c.getContext('2d').drawImage(img, 0, 0);
          return c;
        } finally { URL.revokeObjectURL(url); }
      } catch (e) { /* fall through */ }
    }

    if (typeof createImageBitmap === 'function') {
      try {
        const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
        const c = document.createElement('canvas');
        c.width = bitmap.width; c.height = bitmap.height;
        c.getContext('2d').drawImage(bitmap, 0, 0);
        if (bitmap.close) bitmap.close();
        return c;
      } catch (_) { /* old Safari etc. — fall through */ }
    }

    const orientation = await readJpegExifOrientation(file).catch(() => 1);
    const url = URL.createObjectURL(file);
    try {
      const img = await loadImage(url);
      return applyOrientation(img, orientation || 1);
    } finally { URL.revokeObjectURL(url); }
  }

  // Minimal JPEG EXIF Orientation reader — returns a value 1..8 (1 = default).
  async function readJpegExifOrientation(file) {
    if (!file) return 1;
    const isJpeg = /jpeg/i.test(file.type || '') || /\.jpe?g$/i.test(file.name || '');
    if (!isJpeg) return 1;
    const buf = await file.slice(0, 131072).arrayBuffer();
    const v = new DataView(buf);
    if (v.byteLength < 4 || v.getUint16(0) !== 0xFFD8) return 1;
    let p = 2;
    while (p < v.byteLength - 4) {
      const marker = v.getUint16(p);
      if (marker === 0xFFE1) {
        // Verify "Exif\0\0"
        if (v.getUint32(p + 4) !== 0x45786966 || v.getUint16(p + 8) !== 0) return 1;
        const tiff = p + 10;
        const little = v.getUint16(tiff) === 0x4949;
        const ifd = tiff + v.getUint32(tiff + 4, little);
        const count = v.getUint16(ifd, little);
        for (let i = 0; i < count; i++) {
          const e = ifd + 2 + i * 12;
          if (v.getUint16(e, little) === 0x0112) return v.getUint16(e + 8, little) || 1;
        }
        return 1;
      }
      if ((marker & 0xFF00) !== 0xFF00) break;
      const segLen = v.getUint16(p + 2);
      if (segLen < 2) break;
      p += 2 + segLen;
    }
    return 1;
  }

  function applyOrientation(img, o) {
    const c = document.createElement('canvas');
    const swap = o >= 5 && o <= 8;
    c.width = swap ? img.height : img.width;
    c.height = swap ? img.width : img.height;
    const ctx = c.getContext('2d');
    switch (o) {
      case 2: ctx.translate(c.width, 0); ctx.scale(-1, 1); break;
      case 3: ctx.translate(c.width, c.height); ctx.rotate(Math.PI); break;
      case 4: ctx.translate(0, c.height); ctx.scale(1, -1); break;
      case 5: ctx.rotate(0.5 * Math.PI); ctx.scale(1, -1); break;
      case 6: ctx.rotate(0.5 * Math.PI); ctx.translate(0, -c.width); break;
      case 7: ctx.rotate(0.5 * Math.PI); ctx.translate(c.height, -c.width); ctx.scale(-1, 1); break;
      case 8: ctx.rotate(-0.5 * Math.PI); ctx.translate(-c.height, 0); break;
    }
    ctx.drawImage(img, 0, 0);
    return c;
  }

  // ─── Queue rendering ────────────────────────────────────────────────────
  function renderQueue() {
    const queue = $('[data-queue]');
    const list = $('[data-queue-list]');
    const count = $('[data-queue-count]');
    if (!queue || !list) return;

    list.innerHTML = '';
    state.queue.forEach(item => {
      const row = document.createElement('div');
      row.className = 'ocr-queue-item status-' + item.status;
      if (item.status === 'done') row.classList.add('done');
      if (item.status === 'failed') row.classList.add('failed');
      row.dataset.fileId = item.id;
      row.draggable = state.queue.length > 1;
      const thumb = document.createElement('div');
      thumb.className = 'ocr-queue-thumb';
      if (item.thumbUrl) {
        const img = document.createElement('img');
        img.src = item.thumbUrl; thumb.appendChild(img);
      } else {
        thumb.classList.add('icon'); thumb.textContent = '📄';
      }
      const body = document.createElement('div');
      const name = document.createElement('div');
      name.className = 'ocr-queue-name';
      name.textContent = item.name;
      const meta = document.createElement('div');
      meta.className = 'ocr-queue-meta';
      const mb = (item.size / 1048576).toFixed(item.size > 1048576 ? 1 : 2);
      meta.textContent = mb + ' MB';
      if (item.pdfPageCount) meta.textContent += ' · ' + plural(T().result_pages, item.pdfPageCount);
      if (item.result && item.result.pages) meta.textContent += ' · ' + plural(T().result_pages, item.result.pages.length);
      body.appendChild(name); body.appendChild(meta);

      // Page-range picker for BIG PDFs (>PDF_RANGE_THRESHOLD pages). Rasterizing
      // a 600-page scanned book whole would OOM the tab — the range keeps memory
      // bounded and lets the user OCR exactly the chapter they need.
      // Also on a finished file: another span of the same book is the next run.
      if (item.range && item.status !== 'processing') {
        const range = document.createElement('div');
        range.className = 'ocr-queue-range';
        const label = document.createElement('span');
        label.textContent = (T().range_label || 'OCR pages');
        const fromIn = document.createElement('input');
        fromIn.type = 'number'; fromIn.min = '1'; fromIn.max = String(item.pdfPageCount); fromIn.value = String(item.range.from);
        const dash = document.createElement('span'); dash.textContent = '–';
        const toIn = document.createElement('input');
        toIn.type = 'number'; toIn.min = '1'; toIn.max = String(item.pdfPageCount); toIn.value = String(item.range.to);
        const clampRange = () => {
          let f = Math.max(1, Math.min(parseInt(fromIn.value, 10) || 1, item.pdfPageCount));
          let t = Math.max(f, Math.min(parseInt(toIn.value, 10) || f, item.pdfPageCount));
          // hard span cap keeps the render memory bounded no matter what's typed —
          // and the plan's page ceiling under it (pagesCap)
          const span = pagesCap();
          if (t - f + 1 > span) t = f + span - 1;
          item.range.from = f; item.range.to = t;
          fromIn.value = String(f); toIn.value = String(t);
          precheckQueue();
          syncSubmit();
        };
        fromIn.addEventListener('change', clampRange);
        toIn.addEventListener('change', clampRange);
        [fromIn, toIn].forEach(inp => inp.addEventListener('click', e => e.stopPropagation()));
        range.appendChild(label); range.appendChild(fromIn); range.appendChild(dash); range.appendChild(toIn);
        body.appendChild(range);
      }

      const status = document.createElement('div');
      status.className = 'ocr-queue-status ' + item.status;
      status.textContent = labelForStatus(item.status);

      const remove = document.createElement('button');
      remove.className = 'ocr-queue-remove';
      remove.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>';
      remove.addEventListener('click', e => {
        e.stopPropagation();
        const idx = state.queue.findIndex(q => q.id === item.id);
        if (idx >= 0) {
          if (state.queue[idx].thumbUrl && state.queue[idx].thumbUrl.startsWith('blob:')) URL.revokeObjectURL(state.queue[idx].thumbUrl);
          state.queue.splice(idx, 1);
        }
        queueChanged();
      });

      row.appendChild(thumb);
      row.appendChild(body);
      row.appendChild(status);
      row.appendChild(remove);

      row.addEventListener('click', () => {
        if (item.result) showResult(item);
      });

      list.appendChild(row);
    });

    if (count) count.textContent = plural(T().queue_total, state.queue.length);
    queue.style.display = state.queue.length ? '' : 'none';
  }

  function labelForStatus(s) {
    switch (s) {
      case 'pending': return T().queue_pending;
      case 'processing': return T().queue_processing;
      case 'done': return T().queue_done;
      case 'failed': return T().queue_failed;
      default: return s;
    }
  }

  function updateSections() {
    const hasFiles = state.queue.length > 0;
    $('[data-controls]').style.display = hasFiles ? '' : 'none';
    $('[data-action-bar]').style.display = hasFiles ? '' : 'none';
    const doneResults = state.queue.filter(q => q.status === 'done' && q.result);
    $('[data-export]').style.display = doneResults.length ? '' : 'none';
    $('[data-export]').classList.toggle('active', doneResults.length > 0);
    // «All files in a ZIP» packs a batch; for one result it is a box around one file.
    $$('[data-batch-only]').forEach(b => { b.hidden = doneResults.length < 2; });
  }

  // ─── Language selector ─────────────────────────────────────────────────
  function bindLanguage() {
    const trigger = $('[data-lang-trigger]');
    const panel = $('[data-lang-panel]');
    const search = $('[data-lang-search]');
    if (!trigger || !panel) return;

    trigger.addEventListener('click', e => {
      e.stopPropagation();
      panel.classList.toggle('active');
      if (panel.classList.contains('active') && search) setTimeout(() => search.focus(), 50);
    });

    document.addEventListener('click', e => {
      if (!panel.contains(e.target) && !trigger.contains(e.target)) panel.classList.remove('active');
    });

    if (search) search.addEventListener('input', e => {
      const q = e.target.value.toLowerCase().trim();
      $$('.ocr-lang-opt').forEach(opt => {
        const match = !q || opt.dataset.searchKey.includes(q);
        opt.style.display = match ? '' : 'none';
      });
      // Group headers and options are flat siblings: a header owns everything
      // up to the next header. Hide headers whose options all filtered out.
      $$('.ocr-lang-group').forEach(g => {
        let anyVisible = false;
        for (let el = g.nextElementSibling; el && !el.classList.contains('ocr-lang-group'); el = el.nextElementSibling) {
          if (el.classList.contains('ocr-lang-opt') && el.style.display !== 'none') { anyVisible = true; break; }
        }
        g.style.display = anyVisible ? '' : 'none';
      });
    });
  }

  function renderLanguagePanel() {
    const list = $('[data-lang-list]');
    if (!list) return;
    list.innerHTML = '';

    // Auto option
    const autoOpt = makeLangOpt('auto', T().lang_auto, false);
    list.appendChild(autoOpt);

    LANGS.forEach(([groupId, items]) => {
      const g = document.createElement('div');
      g.className = 'ocr-lang-group';
      g.textContent = T()['lang_group_' + groupId] || groupId;
      list.appendChild(g);
      items.forEach(([code, name]) => list.appendChild(makeLangOpt(code, name, false)));
    });
  }

  function makeLangOpt(code, name, isCached) {
    const el = document.createElement('div');
    el.className = 'ocr-lang-opt';
    el.dataset.code = code;
    el.dataset.searchKey = (code + ' ' + name).toLowerCase();
    if (state.selectedLangs.includes(code)) el.classList.add('selected');

    const check = document.createElement('span');
    check.className = 'ocr-lang-opt-check';
    check.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"><polyline points="20 6 9 17 4 12"/></svg>';
    const label = document.createElement('span');
    label.className = 'ocr-lang-opt-name';
    label.textContent = name;
    el.appendChild(check);
    el.appendChild(label);
    if (isCached || state.loadedLangs.has(code)) {
      const c = document.createElement('span');
      c.className = 'ocr-lang-opt-cached';
      c.textContent = T().lang_cached || 'Cached';
      el.appendChild(c);
    }
    el.addEventListener('click', e => {
      e.stopPropagation();
      toggleLang(code);
    });
    return el;
  }

  function toggleLang(code) {
    if (code === 'auto') {
      state.selectedLangs = ['auto'];
    } else {
      // Remove 'auto' when user picks specific langs
      state.selectedLangs = state.selectedLangs.filter(c => c !== 'auto');
      if (state.selectedLangs.includes(code)) {
        state.selectedLangs = state.selectedLangs.filter(c => c !== code);
        if (!state.selectedLangs.length) state.selectedLangs = ['auto'];
      } else {
        if (state.selectedLangs.length >= MAX_LANGS) {
          state.selectedLangs.shift(); // drop oldest
        }
        state.selectedLangs.push(code);
      }
    }
    renderLanguagePanel();
    renderSelectedLangs();
    saveSetting('langs', state.selectedLangs);
    settingsChanged();
  }

  // A finished file read with other settings is work again (needsRun) — the
  // button and the cap note follow at once, not at the next press.
  function settingsChanged() {
    syncPreprocessPanel();
    precheckQueue();
    syncSubmit();
  }

  function renderSelectedLangs() {
    const holder = $('[data-lang-selected]');
    if (!holder) return;
    holder.innerHTML = '';
    state.selectedLangs.forEach(code => {
      const tag = document.createElement('span');
      tag.className = 'ocr-lang-tag';
      tag.textContent = code === 'auto' ? (T().lang_auto || 'Auto') : (LANG_LOOKUP[code] || code);
      if (code !== 'auto' || state.selectedLangs.length > 1) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.textContent = '×';
        btn.addEventListener('click', e => { e.stopPropagation(); toggleLang(code); });
        tag.appendChild(btn);
      }
      holder.appendChild(tag);
    });
  }

  // ─── Mode / preprocessing ──────────────────────────────────────────────
  function bindMode() {
    $$('[data-mode-val]').forEach(card => {
      card.addEventListener('click', () => {
        $$('[data-mode-val]').forEach(c => c.classList.remove('active'));
        card.classList.add('active');
        state.mode = card.dataset.modeVal;
        saveSetting('mode', state.mode);
        settingsChanged();
      });
    });
  }

  function bindPreprocess() {
    const toggle = $('[data-pp-toggle]');
    const panel = $('[data-pp-panel]');
    if (toggle && panel) {
      toggle.addEventListener('click', () => {
        toggle.classList.toggle('active');
        panel.classList.toggle('active');
      });
    }
    $$('[data-pp]').forEach(cb => {
      cb.addEventListener('change', () => {
        state.preprocess[cb.dataset.pp] = cb.checked;
        if (cb.dataset.pp === 'auto' && cb.checked) {
          // Auto disables manual flags
          $$('[data-pp]').forEach(other => { if (other !== cb) other.checked = false; });
          state.preprocess = { auto: true };
        } else if (cb.checked) {
          // Manual choice disables auto
          const autoCb = document.querySelector('[data-pp="auto"]');
          if (autoCb) autoCb.checked = false;
          state.preprocess.auto = false;
        }
        saveSetting('preprocess', state.preprocess);
        settingsChanged();
      });
    });
  }

  // ─── Main processing ────────────────────────────────────────────────────
  function bindSubmit() {
    const btn = $('[data-submit]');
    const cancel = $('[data-cancel]');
    if (btn) btn.addEventListener('click', runBatch);
    if (cancel) cancel.addEventListener('click', () => {
      state.cancelled = true;
      // Hard-stop the in-flight recognize too: tesseract.js has no per-job
      // abort, so terminate the pool — the pending addJob rejects immediately
      // instead of finishing a 30 s page the user no longer wants. The next
      // run recreates workers (language data is HTTP/SW-cached, so it's cheap).
      if (state.scheduler) {
        try { state.scheduler.terminate(); } catch (e) {}
        state.scheduler = null; state.schedulerKey = null; state.workers = null; state.appliedPsm = null;
      }
      if (state.engine) {
        try { state.engine.close(); } catch (e) {}
        state.engine = null; state.engineKey = null;
      }
    });
  }

  // ── Engine choice ──────────────────────────────────────────────────────
  // The neural lane serves when the shared door knows the script; otherwise
  // (Hebrew, Georgian, vertical CJK, two non-Latin scripts at once) the
  // Tesseract pool below runs exactly as it always has.
  function neuralServes(langs) {
    return !!(window.OcrEngine && window.PPOCR && window.OcrEngine.supports(langs));
  }
  async function ensureEngine(langs) {
    const key = langs.slice().sort().join('+');
    if (state.engine && state.engineKey === key) return state.engine;
    if (state.engine) { try { state.engine.close(); } catch (e) {} state.engine = null; }
    setProgressStage(T().stage_loading_model.replace('{pct}', '0'));
    const t0 = performance.now();
    state.engine = await window.OcrEngine.open(langs, {
      slug: 'ocr-text-extractor',
      workerUrl: window.__PPOCR_WORKER_URL || undefined,
      onProgress: (f) => setProgressStage(T().stage_loading_model.replace('{pct}', Math.round(f * 100))),
    });
    state.engineKey = key;
    ae('ocr_lang_loaded', { langs: key, engine: state.engine.kind, provider: state.engine.provider || '', ms: Math.round(performance.now() - t0) });
    return state.engine;
  }

  async function runBatch() {
    if (state.isProcessing) return;
    fitRanges();
    const pending = state.queue.filter(needsRun);
    if (!pending.length) { syncSubmit(); return; }
    // The daily cap. begin() is the RESERVATION — can this queue be afforded at
    // all (refused → the shared note is up, nothing spent); the bill is what
    // came out: files that gave text (see the finally below). A repeat of a
    // file already counted today — another language, another style — is free.
    const TL = window.__toolLimit;
    const limitRun = TL ? TL.begin(LIMIT_SLUG, pending.map(limitKeyOf)) : null;
    if (TL && !limitRun) return;
    const rerun = pending.filter(q => q.status === 'done' && q.result).length;
    if (rerun) ae('feature_use', { slug: LIMIT_SLUG, action: 'rerun_new_settings', files: rerun });
    state.isProcessing = true;
    state.cancelled = false;
    const submitBtn = $('[data-submit]');
    const cancelBtn = $('[data-cancel]');
    submitBtn.disabled = true;
    if (cancelBtn) cancelBtn.style.display = '';

    const progress = $('[data-progress]');
    progress.classList.add('active');

    // Prechecks — both MUST run before ensureScheduler() spawns the first
    // tesseract worker (the probe worker in ensureProbeWorker() is downstream
    // of it, so this is the single choke point).
    //  1) Tesseract itself never loaded (CDN block).
    //  2) WebAssembly is switched off in this browser (Sentry TIMBRICA-FRONTEND-DR:
    //     Chrome 147 / Linux, `WebAssembly is not defined`). tesseract.js only
    //     finds that out INSIDE its worker (the core loader probes
    //     WebAssembly.validate), and its main-thread onmessage rethrows the
    //     worker's reject as an UNCAUGHT `Error: ReferenceError: WebAssembly is
    //     not defined` — a worker-global failure nothing here can catch, on top
    //     of the rejected createWorker(). Refuse before the spawn, same as
    //     chess-stockfish.js does before `new Worker()`.
    const langsWanted = resolveLangs();
    const viaNeural = neuralServes(langsWanted);
    const precheckFail = (!viaNeural && typeof Tesseract === 'undefined') ? 'missing_tesseract'
      : typeof WebAssembly === 'undefined' ? 'wasm_unavailable'
      : null;
    if (precheckFail) {
      if (precheckFail === 'wasm_unavailable') {
        // Browser capability, not a bug in our code — keep tool_error meaningful.
        ae('tool_blocked', { slug: 'ocr-text-extractor', reason: precheckFail });
      } else {
        ae('tool_error', { slug: 'ocr-text-extractor', error_type: precheckFail });
      }
      toast(T().err_model_load, 'error');
      state.isProcessing = false;
      submitBtn.disabled = false;
      if (cancelBtn) cancelBtn.style.display = 'none';
      progress.classList.remove('active');
      if (limitRun) limitRun.abort();   // nothing was read — nothing is spent
      syncSubmit();
      return;
    }

    const batchStart = performance.now();
    const langs = langsWanted;
    ae('ocr_start', {
      files: pending.length,
      langs: langs.join('+'),
      mode: state.mode,
      engine: viaNeural ? 'neural' : 'tesseract',
      preprocess: Object.keys(state.preprocess).filter(k => state.preprocess[k]).join(','),
    });
    funnel('processing', { files: pending.length, mode: state.mode });

    try {
      try {
        if (viaNeural) await ensureEngine(langs); else await ensureScheduler(langs);
      } catch (e0) {
        // The engine itself did not open (worker script or model did not
        // arrive — a network/CDN fact, not a document failure): the one line
        // the person can act on, and a typed event instead of a generic one.
        const err = asError(e0);
        ae('tool_error', { slug: 'ocr-text-extractor', error_type: 'engine_open_failed', engine: viaNeural ? 'neural' : 'tesseract', code: err.code || '', msg: (err.message || '').slice(0, 200) });
        toast(T().err_model_load, 'error');
        return;
      }
      const total = pending.length;
      let doneCount = 0, failCount = 0, totalWords = 0, totalPages = 0, totalConf = 0, langHintShown = false;
      for (let i = 0; i < pending.length; i++) {
        if (state.cancelled) break;
        const item = pending[i];
        item.status = 'processing';
        renderQueue();
        setProgressTitle(`${item.name} — ${i + 1}/${total}`);
        const itemStart = performance.now();
        try {
          const result = await processItem(item, langs);
          item.result = result;
          item.status = 'done';
          item.sig = runSig();
          item.doneRange = rangeSig(item);
          try { window.__lastOcrEngine = result.engine; } catch (_) {}   // read by the real-corpus battery
          // Zero words is a DONE state the user must understand, not silence:
          // wrong language pick, an empty page, or a photo the engine can't
          // read. Surface the dead err_empty_result path with actionable hints.
          if (!result.words.length) {
            toast(T().err_empty_result || 'No text detected. Check the language selection, or try the Binarize preprocess option for photos.', 'warning');
            ae('feature_use', { slug: 'ocr-text-extractor', action: 'empty_result', lang: langs.join('+'), mode: state.mode });
          } else if (result.confidence < 60 && !langHintShown) {
            // Garbage-in-garbage-out guard for the honest-language default:
            // low confidence across a whole file usually means the document is
            // in a language outside the current selection. One hint per batch.
            langHintShown = true;
            toast(T().hint_wrong_lang || 'Low confidence — the document may be in another language. Pick it in the language list.', 'info');
            ae('feature_use', { slug: 'ocr-text-extractor', action: 'low_conf_lang_hint', lang: langs.join('+'), conf: Math.round(result.confidence) });
          }
          // Receipt-mode parser: surface structured fields for invoices/receipts.
          if (state.mode === 'receipt' && window.OcrReceipt) {
            try { result.parsed = window.OcrReceipt.parse(result); } catch (_) {}
          }
          doneCount++;
          totalWords += result.words.length;
          totalPages += result.pageCount;
          totalConf += result.confidence;
          ae('ocr_page_done', {
            pages: result.pageCount,
            words: result.words.length,
            engine: result.engine || (viaNeural ? 'neural' : 'tesseract'),
            provider: result.provider || '',
            conf: Math.round(result.confidence),
            ms: Math.round(performance.now() - itemStart),
            size_mb: +(item.size / 1048576).toFixed(2),
          });
          saveSession();
        } catch (e0) {
          const err = asError(e0);
          // A cancel mid-recognize rejects the in-flight job — that's the user
          // stopping, not a document failure. Leave the item pending so the
          // next run picks it up where the batch left off.
          if (state.cancelled) { item.status = 'pending'; renderQueue(); break; }
          handleError(err, 'ocr.process');
          item.status = 'failed';
          item.error = err.message || 'Failed';
          failCount++;
          ae('tool_error', {
            slug: 'ocr-text-extractor',
            error_type: 'extract_failed',
            msg: (err.message || '').slice(0, 200),
            ms: Math.round(performance.now() - itemStart),
          });
        }
        renderQueue();
        // A re-read of the file on screen replaces what is shown.
        if (item.result && item.status === 'done' && (!state.activeResult || state.activeResult === item)) showResult(item);
      }
      if (!state.cancelled) {
        const firstDone = state.queue.find(q => q.status === 'done');
        if (firstDone && !state.activeResult) showResult(firstDone);
      }
      if (doneCount > 0) funnel('result', { files: doneCount, words: totalWords });
      ae('ocr_batch_done', {
        files_done: doneCount,
        files_failed: failCount,
        pages: totalPages,
        words: totalWords,
        avg_conf: doneCount ? Math.round(totalConf / doneCount) : 0,
        ms: Math.round(performance.now() - batchStart),
      });
    } catch (e0) {
      const err = asError(e0);
      handleError(err, 'ocr.batch');
      ae('tool_error', {
        slug: 'ocr-text-extractor',
        error_type: 'batch_failed',
        msg: (err.message || '').slice(0, 200),
      });
      toast(err.message || T().err_extract_generic, 'error');
    } finally {
      state.isProcessing = false;
      submitBtn.disabled = false;
      if (cancelBtn) cancelBtn.style.display = 'none';
      progress.classList.remove('active');
      if (limitRun) {
        // Settle the reservation by what was delivered: a file that failed, was
        // cancelled before its turn or held no text spends nothing.
        limitRun.abort();
        const given = pending.filter(q => q.status === 'done' && q.result && q.result.words && q.result.words.length).map(limitKeyOf);
        if (given.length) TL.record(LIMIT_SLUG, given);
        if (TL.refresh) TL.refresh(LIMIT_SLUG);
      }
      queueChanged();
    }
  }

  // UI locale → tesseract lang code, for the honest "auto" default. Pre-fix
  // "auto" silently meant English-only — a Russian user OCRing a Russian scan
  // got garbage with no hint why. Now "auto" = the site language + English,
  // which matches what the visitor is overwhelmingly likely to upload.
  const LOCALE_TO_TESS = { ru: 'rus', de: 'deu', es: 'spa', fr: 'fra', uk: 'ukr', pt: 'por', id: 'ind', ko: 'kor', ar: 'ara', en: 'eng', tr: 'tur' };
  function resolveLangs() {
    if (state.selectedLangs.includes('auto') || !state.selectedLangs.length) {
      const localeLang = LOCALE_TO_TESS[(window.__LOCALE || 'en').slice(0, 2)] || 'eng';
      return localeLang === 'eng' ? ['eng'] : [localeLang, 'eng'];
    }
    return state.selectedLangs;
  }

  function tessWorkerOpts() {
    const workerOpts = {};
    if (TESSDATA_CDN) workerOpts.langPath = TESSDATA_CDN;
    if (WORKER_PATH) workerOpts.workerPath = WORKER_PATH;
    if (CORE_PATH) workerOpts.corePath = CORE_PATH;
    // Без errorHandler tesseract's own onmessage THROWS uncaught in the main
    // thread on a worker-side reject it cannot route (3rd mechanism of Sentry
    // DR: WASM «indirect call to null» on Firefox, after WebAssembly-undefined
    // and toBlob-null). Our promise catches already surface the per-file
    // error UX — this only closes the duplicate uncaught channel.
    workerOpts.errorHandler = (err) => {
      try { console.warn('[ocr] tesseract worker error:', err); } catch (_) {}
    };
    return workerOpts;
  }

  async function ensureScheduler(langs) {
    const key = langs.slice().sort().join('+');
    if (state.scheduler && state.schedulerKey === key) return state.scheduler;

    if (state.scheduler) {
      try { await state.scheduler.terminate(); } catch (e) {}
    }
    setProgressStage(T().stage_loading_model.replace('{pct}', '0'));
    const langLoadStart = performance.now();
    state.scheduler = Tesseract.createScheduler();
    state.workers = [];   // direct refs — scheduler has no parameter API, setParameters goes per-worker
    const langsStr = langs.join('+');
    const workerOpts = tessWorkerOpts();
    for (let i = 0; i < WORKER_POOL_SIZE; i++) {
      const worker = await Tesseract.createWorker(langsStr, 1, {
        ...workerOpts,
        logger: (m) => {
          if (m.status && m.status.startsWith('loading language')) {
            setProgressStage(T().stage_loading_lang.replace('{pct}', Math.round((m.progress || 0) * 100)));
          } else if (m.status === 'recognizing text') {
            setProgressStage(T().stage_extracting.replace('{pct}', Math.round((m.progress || 0) * 100)));
          } else if (m.status === 'loading tesseract core') {
            setProgressStage(T().stage_loading_model.replace('{pct}', Math.round((m.progress || 0) * 100)));
          }
        },
      });
      state.scheduler.addWorker(worker);
      state.workers.push(worker);
    }
    state.schedulerKey = key;
    state.appliedPsm = null;   // force re-apply for the new worker set
    const newLangs = langs.filter(c => !state.loadedLangs.has(c));
    langs.forEach(c => state.loadedLangs.add(c));
    if (newLangs.length) {
      ae('ocr_lang_loaded', {
        langs: newLangs.join('+'),
        ms: Math.round(performance.now() - langLoadStart),
      });
    }
    return state.scheduler;
  }

  // Page-segmentation mode. Measured head-to-head on the battery corpus
  // (2026-07-02): tesseract.js v5's DEFAULT is SINGLE_BLOCK (PSM 6), and that
  // default returns ZERO words on bordered tables; PSM 3 (full auto layout
  // analysis) reads the same table at 15 words / 94% conf, ties PSM 6 on
  // receipts and plain paragraphs, and handles multi-column layouts. So: PSM 3
  // everywhere — the tool must never again silently run on the v5 default.
  // (PSM 6 measured 0 words on the table; PSM 11 fragments receipts — keep 3.)
  function psmForMode(mode) {
    return '3';
  }

  // tesseract.js v5: recognize() options do NOT carry tessedit_pageseg_mode —
  // parameters must be set per worker. Applied once per run (mode is global at
  // submit time); memoised so consecutive same-mode runs skip the round-trip.
  async function applyModeParams() {
    const psm = psmForMode(state.mode);
    if (state.appliedPsm === psm || !state.workers) return;
    await Promise.all(state.workers.map(w => w.setParameters({ tessedit_pageseg_mode: psm }).catch(() => {})));
    state.appliedPsm = psm;
  }

  // ── Content-based orientation probe ─────────────────────────────────────
  // EXIF only fixes camera metadata; a sideways SCAN or photo has orientation=1
  // and Tesseract outputs garbage (battery: rotated-90 → 46% conf, 0 tokens).
  // Probe: recognize a ≤800px downscale at 0/90/180/270 on a DEDICATED sparse
  // worker (own PSM 11 — never fights the pool's per-mode PSM), score each by
  // words×meanConf, rotate the source to the winner. Images: probe per file;
  // PDFs: probe page 1, apply to the whole document (books are uniformly
  // oriented; per-page probing would cost 4 recognitions × N pages).
  async function ensureProbeWorker(langs) {
    const key = langs.slice().sort().join('+');
    if (state.probeWorker && state.probeWorkerKey === key) return state.probeWorker;
    if (state.probeWorker) { try { await state.probeWorker.terminate(); } catch (e) {} state.probeWorker = null; }
    const w = await Tesseract.createWorker(langs.join('+'), 1, tessWorkerOpts());
    await w.setParameters({ tessedit_pageseg_mode: '11' });   // SPARSE_TEXT — cheapest usable probe
    state.probeWorker = w;
    state.probeWorkerKey = key;
    return w;
  }

  function rotateCanvasQuarter(src, deg) {
    const c = document.createElement('canvas');
    if (deg === 180) { c.width = src.width; c.height = src.height; }
    else { c.width = src.height; c.height = src.width; }
    const ctx = c.getContext('2d');
    ctx.translate(c.width / 2, c.height / 2);
    ctx.rotate(deg * Math.PI / 180);
    ctx.drawImage(src, -src.width / 2, -src.height / 2);
    return c;
  }

  async function probeOrientation(canvas, langs) {
    try {
      const worker = await ensureProbeWorker(langs);
      // downscale() returns the SAME canvas when it already fits — never wipe
      // an alias of the source (a ≤800px fax was being destroyed here before
      // its real recognition even started: battery lowdpi-fax 2/2 → 0/2).
      const small = downscale(canvas, 800);
      const ownSmall = small !== canvas;
      let best = { deg: 0, score: 0 };
      const scores = {};
      for (const deg of [0, 90, 180, 270]) {
        if (state.cancelled) break;
        const probe = deg === 0 ? small : rotateCanvasQuarter(small, deg);
        try {
          const { data } = await worker.recognize(probe);
          const words = (data.words || []).filter(x => x.text && x.text.trim().length > 1 && (x.confidence || 0) > 40);
          const meanConf = words.length ? words.reduce((a, x) => a + x.confidence, 0) / words.length : 0;
          scores[deg] = Math.round(words.length * meanConf);
        } catch (_) { scores[deg] = 0; }
        if (probe !== small) { probe.width = probe.height = 1; }
        if (scores[deg] > best.score) best = { deg, score: scores[deg] };
      }
      if (ownSmall) { small.width = small.height = 1; }
      // Require a clear win over 0° — never rotate on noise (0° keeps ties).
      if (best.deg !== 0 && best.score > (scores[0] || 0) * 1.3) {
        ae('ocr_auto_rotate', { deg: best.deg, s0: scores[0] || 0, sbest: best.score });
        return best.deg;
      }
      return 0;
    } catch (_) { return 0; }
  }

  async function processItem(item, langs) {
    const file = item.file;
    const isPdf = await window.OcrPdf.isPdfFile(file);
    const pages = [];
    // Content-based orientation is decided on the FIRST page that streams in
    // (cheap 4-way sparse recognition) and applied to the whole document —
    // books/scans are uniformly oriented, per-page probing would cost 4× N.
    let rotation = null;
    let firstPage = true;
    let readOn = '';        // the provider that actually read the pages (the engine may step down mid-file)

    // door = the shared OcrEngine serves this batch (neural, or its own
    // Tesseract fallback); otherwise the pool below runs as it always has.
    const door = !!state.engine;
    const neural = door && state.engine.kind === 'neural';
    if (!door) await applyModeParams();   // PSM per output mode — explicit, never the v5 default

    // Per-page pipeline. PDFs STREAM through this via renderPages(onPage) so
    // peak memory is ~1 page (a 60-page book held ~0.5 GB of canvases pre-fix);
    // single images call it once directly.
    const handleSource = async (src) => {
      if (state.cancelled) throw new Error('Cancelled');
      let canvas = src.canvas;

      if (rotation === null) {
        setProgressStage(T().stage_preprocessing);
        rotation = door ? await state.engine.probeOrientation(canvas) : await probeOrientation(canvas, langs);
      }
      if (rotation !== 0) {
        const rotated = rotateCanvasQuarter(canvas, rotation);
        if (canvas !== src.canvas) { canvas.width = canvas.height = 1; }
        canvas = rotated;
      }
      setProgressStage(T().stage_preprocessing);

      // ORIGINAL snapshot (post-rotation, pre-preprocess) — this is what the
      // searchable PDF embeds. Binarize/invert must never leak into exports
      // (pre-fix the searchable PDF shipped the black-and-white processed
      // image instead of the user's document).
      const originalDataUrl = canvas.toDataURL('image/jpeg', 0.82);
      const originalWidth = canvas.width, originalHeight = canvas.height;

      // On a memory-starved phone the copy below is the step that dies: a
      // second full-size canvas cannot be backed, and what reached Tesseract
      // was an unusable surface — which surfaced to the user as a bare
      // "readAsArrayBuffer: parameter 1 is not of type 'Blob'" from deep
      // inside the engine (Sentry 138706395, TECNO KL7 / Android 14).
      // Fall back to the ORIGINAL canvas: OCR without preprocessing beats
      // OCR with a cryptic crash.
      // Measured 12.09.2026 on real phone pages: OcrPre's autocrop+deskew made the
      // neural engine WORSE (39–86 % → 56–94 % CER; a grey desk around the page
      // reads as ink to the projection-profile deskew). The neural lane gets
      // the pixels untouched; geometry is its own job (see ppocr-worker).
      const imgData = neural ? null : applyPreprocessing(canvas);
      let processedCanvas = canvas;
      if (imgData) {
        try {
          const pc = document.createElement('canvas');
          pc.width = imgData.width; pc.height = imgData.height;
          pc.getContext('2d').putImageData(imgData, 0, 0);
          if (pc.width > 0 && pc.height > 0) processedCanvas = pc;
        } catch (e) {
          handleError(e, 'preprocess');
          ae('tool_error', { slug: 'ocr-text-extractor', error_type: 'preprocess_canvas_alloc', w: imgData.width, h: imgData.height });
        }
      }
      // Never hand the engine a surface it cannot read.
      if (!processedCanvas || !processedCanvas.width || !processedCanvas.height) {
        throw new Error(T().err_memory || 'Not enough memory. Try fewer pages or a smaller image.');
      }

      let data;
      if (door) {
        // The neural lane takes the canvas as an ImageBitmap — no Blob is ever
        // made, so the toBlob-null class (Sentry D3) does not exist here; the
        // door's own Tesseract fallback makes its Blob where null is checkable.
        setProgressStage(T().stage_extracting.replace('{pct}', '0'));
        data = await state.engine.recognize(processedCanvas, {
          onLine: (l) => { if (l && l.total) setProgressStage(T().stage_extracting.replace('{pct}', Math.round(l.index / l.total * 100))); },
        });
        if (data && data.provider) readOn = data.provider;
      } else {
        // tesseract.js turns a canvas into a Blob via canvas.toBlob() INTERNALLY,
        // and on a memory-starved device toBlob hands its callback NULL (the
        // spec's encode-failure path — seen on Opera, Sentry D3). That null then
        // reaches FileReader.readAsArrayBuffer deep inside the engine and the
        // whole batch dies with «parameter 1 is not of type 'Blob'». Do the same
        // conversion HERE, where null is checkable, and fail only this FILE —
        // the per-item catch in runBatch marks it failed and carries on.
        const jobBlob = await new Promise((resolve) => {
          try { processedCanvas.toBlob(resolve, 'image/png'); } catch (e) { resolve(null); }
        });
        if (!jobBlob || !jobBlob.size) {
          ae('tool_error', { slug: 'ocr-text-extractor', error_type: 'canvas_to_blob_null', w: processedCanvas.width, h: processedCanvas.height });
          throw new Error(T().err_memory || 'Not enough memory. Try fewer pages or a smaller image.');
        }

        // Run OCR. v5 signature: addJob('recognize', image, OPTIONS, OUTPUT) —
        // the old `{blocks:true}` sat in the options slot where it is a no-op
        // (v5's default output happens to include blocks; a future major bump
        // could flip that default and silently drop every word/bbox).
        data = (await state.scheduler.addJob('recognize', jobBlob, {}, { text: true, blocks: true })).data;
      }

      const words = (data.words || []).map(w => ({
        text: w.text,
        confidence: w.confidence || 0,
        bbox: w.bbox ? { x0: w.bbox.x0, y0: w.bbox.y0, x1: w.bbox.x1, y1: w.bbox.y1 } : null,
      }));
      const text = formatText(data, state.mode);
      // Word bboxes are in PROCESSED coordinates, so the workspace keeps
      // showing the processed image (pixel-perfect box↔word alignment, and
      // honest about what the engine actually read). The ORIGINAL snapshot is
      // carried separately purely for exports.
      pages.push({
        pageNumber: src.pageNumber,
        totalPages: src.totalPages,
        imageDataUrl: processedCanvas.toDataURL('image/jpeg', 0.82),
        originalDataUrl,
        originalWidth,
        originalHeight,
        imageWidth: processedCanvas.width,
        imageHeight: processedCanvas.height,
        text,
        words,
        confidence: data.confidence || 0,
        language: langs.join('+'),
      });

      // QR scan — once per FILE, on the ORIGINAL upright canvas (Sauvola can
      // destroy QR finder patterns; and the pre-fix session-wide latch meant a
      // QR in the second uploaded file was never scanned).
      if (!item.qrScanned && window.jsQR && firstPage) {
        item.qrScanned = true;
        try {
          const smallCanvas = downscale(canvas, 800);
          const sctx = smallCanvas.getContext('2d');
          const sdata = sctx.getImageData(0, 0, smallCanvas.width, smallCanvas.height);
          const code = window.jsQR(sdata.data, sdata.width, sdata.height);
          if (code && code.data) item.qrCode = code.data;
          if (smallCanvas !== canvas) { smallCanvas.width = smallCanvas.height = 1; }
        } catch (e) {}
      }
      firstPage = false;

      // Release page canvases promptly — only the JPEG dataURLs are retained.
      // NEVER wipe an alias of the source: when preprocessing was skipped
      // processedCanvas IS `canvas` (same trap the downscale() comment above
      // documents — a ≤800px fax destroyed before its own recognition).
      if (processedCanvas !== canvas) { processedCanvas.width = processedCanvas.height = 1; }
      if (canvas !== src.canvas) { canvas.width = canvas.height = 1; }
      // (src.canvas itself is released by the streaming renderer / below.)
    };

    if (isPdf) {
      setProgressStage(T().stage_rendering_pdf.replace('{n}', '0').replace('{total}', '?'));
      const rendered = await window.OcrPdf.renderPages(file, {
        scale: 2.0,
        from: item.range ? item.range.from : undefined,
        to: item.range ? item.range.to : undefined,
        onPage: handleSource,   // stream: render page → OCR page → release canvas
        onProgress: (done, total) => setProgressStage(T().stage_rendering_pdf.replace('{n}', done).replace('{total}', total)),
      });
      // Partial success: a corrupt page no longer kills the document — tell the
      // user which pages were skipped and carry on with the rest.
      if (rendered.failedPages.length) {
        toast((T().err_pdf_pages || 'Some PDF pages could not be rendered: {pages}').replace('{pages}', rendered.failedPages.join(', ')), 'warning');
        ae('tool_error', { slug: 'ocr-text-extractor', error_type: 'pdf_pages_partial', failed: rendered.failedPages.length, total: rendered.total });
      }
      if (!pages.length) throw new Error(T().err_pdf_render || 'Could not render this PDF.');
    } else {
      const canvas = await loadImageWithOrientation(file);
      await handleSource({ canvas, pageNumber: 1, totalPages: 1 });
      canvas.width = canvas.height = 1;
    }

    const combinedText = pages.map(p => p.text).join('\n\n');
    const combinedWords = [].concat.apply([], pages.map(p => p.words));
    const avgConf = pages.reduce((a, p) => a + p.confidence, 0) / pages.length;
    return {
      fileName: item.name,
      pages,
      pageCount: pages.length,
      text: combinedText,
      words: combinedWords,
      confidence: avgConf,
      language: langs.join('+'),
      engine: door ? state.engine.kind : 'tesseract',
      provider: readOn || (door ? state.engine.provider || '' : 'wasm'),
    };
  }

  // Returns null when the page cannot be preprocessed on this device — the
  // caller then OCRs the untouched canvas. `getImageData` used to sit OUTSIDE
  // the try even though it is the very call that runs out of memory first on a
  // low-RAM phone ("RangeError: Out of memory at ImageData creation").
  function applyPreprocessing(canvas) {
    try {
      const ctx = canvas.getContext('2d');
      const src = ctx.getImageData(0, 0, canvas.width, canvas.height);
      try {
        return window.OcrPre.process(src, state.preprocess);
      } catch (e) {
        handleError(e, 'preprocess');
        return src;                       // processing failed, pixels are fine
      }
    } catch (e) {
      // Could not even READ the pixels — the device is out of memory. Say so
      // and let the caller fall back instead of manufacturing a broken canvas.
      handleError(e, 'preprocess');
      ae('tool_error', { slug: 'ocr-text-extractor', error_type: 'preprocess_oom', w: canvas.width, h: canvas.height });
      return null;
    }
  }

  function formatText(data, mode) {
    if (mode === 'plain') return (data.text || '').replace(/[\r\n]+/g, ' ').replace(/\s+/g, ' ').trim();
    if (mode === 'tables') {
      const { rows, isTable } = window.OcrExport.detectTables((data.words || []).map(w => ({
        text: w.text,
        bbox: w.bbox,
      })));
      if (isTable && rows) return rows.map(r => r.join('\t')).join('\n');
      return (data.text || '').trim();
    }
    // layout — keep Tesseract's newlines
    return (data.text || '').trim();
  }

  function downscale(canvas, maxDim) {
    const w = canvas.width, h = canvas.height;
    if (Math.max(w, h) <= maxDim) return canvas;
    const scale = maxDim / Math.max(w, h);
    const out = document.createElement('canvas');
    out.width = Math.round(w * scale); out.height = Math.round(h * scale);
    out.getContext('2d').drawImage(canvas, 0, 0, out.width, out.height);
    return out;
  }

  function setProgressStage(msg) {
    const el = $('[data-progress-stage]'); if (el) el.textContent = msg || '';
  }
  function setProgressTitle(msg) {
    const el = $('[data-progress-title]'); if (el) el.textContent = msg || T().processing;
  }

  // ─── Workspace (image + text) ──────────────────────────────────────────
  function bindWorkspaceTabs() {
    $$('[data-ws-view]').forEach(btn => {
      btn.addEventListener('click', () => {
        $$('[data-ws-view]').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        const body = $('[data-ws-body]');
        body.classList.remove('text-only', 'image-only');
        const v = btn.dataset.wsView;
        if (v === 'text') body.classList.add('text-only');
        else if (v === 'image') body.classList.add('image-only');
      });
    });
    const prev = $('[data-page-prev]'), next = $('[data-page-next]');
    if (prev) prev.addEventListener('click', () => gotoPage(-1));
    if (next) next.addEventListener('click', () => gotoPage(1));
    // PageUp/PageDown flip pages when a multi-page result is open and the user
    // isn't typing in an input/contenteditable.
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'PageUp' && e.key !== 'PageDown') return;
      if (!state.activeResult || !state.activeResult.result || state.activeResult.result.pages.length < 2) return;
      const t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      e.preventDefault();
      gotoPage(e.key === 'PageDown' ? 1 : -1);
    });
  }

  // Render page N of the active result into the viewer (image + boxes + text).
  // Multi-page documents were previously locked to page 1 with no navigation.
  function renderActivePage() {
    const item = state.activeResult;
    if (!item || !item.result) return;
    const pages = item.result.pages;
    const idx = Math.max(0, Math.min(state.activePageIdx || 0, pages.length - 1));
    state.activePageIdx = idx;
    const page = pages[idx];

    const img = $('[data-ws-img]');
    img.src = page.imageDataUrl || '';
    img.alt = item.name + (pages.length > 1 ? ' — ' + (idx + 1) + '/' + pages.length : '');
    img.onload = () => renderBboxes(page);

    // Text panel follows the visible page so box↔word click-sync stays true.
    const textEl = $('[data-text]');
    if (textEl) textEl.innerHTML = renderWordsHtml(page.words, page.text || '');

    const nav = $('[data-page-nav]');
    if (nav) {
      const multi = pages.length > 1;
      nav.hidden = !multi;
      if (multi) {
        const label = $('[data-page-label]');
        if (label) label.textContent = (T().queue_page_of || 'Page {n} of {total}')
          .replace('{n}', idx + 1).replace('{total}', pages.length);
        const prev = $('[data-page-prev]'), next = $('[data-page-next]');
        if (prev) prev.disabled = idx === 0;
        if (next) next.disabled = idx >= pages.length - 1;
      }
    }
  }

  function gotoPage(delta) {
    const item = state.activeResult;
    if (!item || !item.result) return;
    const n = item.result.pages.length;
    const next = Math.max(0, Math.min((state.activePageIdx || 0) + delta, n - 1));
    if (next === state.activePageIdx) return;
    state.activePageIdx = next;
    renderActivePage();
    ae('feature_use', { slug: 'ocr-text-extractor', action: 'page_nav', page: next + 1, total: n });
  }

  function showResult(item) {
    if (!item || !item.result) return;
    state.activeResult = item;
    state.activePageIdx = 0;
    const ws = $('[data-ws]');
    ws.classList.add('active');

    renderActivePage();

    // Stats
    const stats = $('[data-ws-stats]');
    const totalWords = item.result.words.length;
    const lang = item.result.language;
    const chars = (item.result.text || '').length;
    stats.innerHTML = '';
    stats.appendChild(stat(plural(T().result_words, totalWords)));
    stats.appendChild(stat(plural(T().result_chars, chars)));
    if (item.result.pageCount > 1) stats.appendChild(stat(plural(T().result_pages, item.result.pageCount)));
    stats.appendChild(stat((T().result_confidence || 'Confidence') + ': ' + Math.round(item.result.confidence) + '%'));
    stats.appendChild(stat((T().result_language || 'Language') + ': ' + lang));

    // Confidence breakdown — counts words by tier (high/medium/low/poor)
    const dist = { high: 0, medium: 0, low: 0, poor: 0 };
    for (const w of item.result.words) {
      const c = w.confidence || 0;
      if (c >= 85) dist.high++;
      else if (c >= 70) dist.medium++;
      else if (c >= 50) dist.low++;
      else dist.poor++;
    }
    if (totalWords > 0) {
      const pct = k => Math.round((dist[k] / totalWords) * 100);
      const distEl = document.createElement('span');
      distEl.className = 'ocr-conf-dist';
      distEl.innerHTML =
        '<i class="ocr-conf-dot ocr-conf-high"></i>' + pct('high') + '%' +
        '<i class="ocr-conf-dot ocr-conf-medium"></i>' + pct('medium') + '%' +
        '<i class="ocr-conf-dot ocr-conf-low"></i>' + (pct('low') + pct('poor')) + '%';
      distEl.title = (T().result_confidence || 'Confidence') + ': ' +
        dist.high + ' high · ' + dist.medium + ' medium · ' + (dist.low + dist.poor) + ' low';
      stats.appendChild(distEl);
    }

    // Text area is rendered per-page inside renderActivePage().

    // Receipt-mode parsed-fields panel
    const parsedEl = $('[data-parsed]');
    if (parsedEl) {
      parsedEl.innerHTML = '';
      const parsed = item.result.parsed;
      if (state.mode === 'receipt' && parsed) {
        const field = (label, val) => val ? `<div class="ocr-parsed-field"><span class="ocr-parsed-label">${label}</span><span class="ocr-parsed-value">${escapeHtml(String(val))}</span></div>` : '';
        const totalDisplay = parsed.total != null
          ? (parsed.currency ? parsed.currency + ' ' : '') + parsed.total.toFixed(2)
          : '';
        const html =
          field(T().receipt_vendor || 'Vendor', parsed.vendor) +
          field(T().receipt_date || 'Date', parsed.date) +
          field(T().receipt_total || 'Total', totalDisplay);
        if (html) {
          parsedEl.innerHTML = html;
          parsedEl.classList.add('active');
        } else {
          parsedEl.innerHTML = '<span class="ocr-parsed-empty">' + (T().receipt_no_data || 'No structured fields detected.') + '</span>';
          parsedEl.classList.add('active');
        }
      } else {
        parsedEl.classList.remove('active');
      }
    }

    // QR banner
    const qrBanner = $('[data-qr-banner]');
    if (item.qrCode) {
      qrBanner.classList.add('active');
      $('[data-qr-content]').textContent = item.qrCode;
    } else {
      qrBanner.classList.remove('active');
    }

    renderPrecise(item);
    ws.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }

  // ─── «Распознать точнее» (Yandex Vision OCR за токены) ─────────────────
  // Одна страница на запрос, свой ключ нажатия (rid) у каждой: страница,
  // ответ на которую не дошёл (обрыв, заморозка вкладки), забирается по ключу
  // без второго списания (OcrPreciseHandler, docs/why/paid-sync-result.md).
  // Списывает сервер, и только за страницу, вернувшуюся с текстом.
  const PRECISE = window.__OCR_PRECISE || null;
  let preciseInfo = null, preciseInfoAt = 0;

  function fmtTokens(n) {
    let s = String(n);
    try { s = Number(n).toLocaleString(window.__intlLocale ? window.__intlLocale() : undefined); } catch (_) {}
    return plural(T().precise_tokens || '{n} tokens', n).replace(String(n), s);
  }

  async function preciseFetchInfo(force) {
    if (!PRECISE) return null;
    if (!force && preciseInfo && Date.now() - preciseInfoAt < 30000) return preciseInfo;
    try {
      const r = await fetch(PRECISE.info, { credentials: 'same-origin', headers: { 'Accept': 'application/json' } });
      if (r.ok) { preciseInfo = await r.json(); preciseInfoAt = Date.now(); }
    } catch (_) {}
    return preciseInfo;
  }

  function preciseState(text, isErr) {
    const el = $('[data-precise-state]');
    if (!el) return;
    el.textContent = text || '';
    el.classList.toggle('err', !!isErr);
  }

  async function renderPrecise(item, force) {
    const box = $('[data-precise]');
    if (!PRECISE || !box) return;
    if (!item || !item.result || state.activeResult !== item) { box.hidden = true; return; }
    const r = item.result;
    const pages = (r.pages && r.pages.length) || 1;
    const go = $('[data-precise-go]'), buy = $('[data-precise-buy]');
    const lowEl = $('[data-precise-low]');
    if (item.preciseDone) {
      box.classList.remove('low');
      if (lowEl) lowEl.hidden = true;
      go.hidden = true; buy.hidden = true;
      $('[data-precise-desc]').textContent = '';
      preciseState((T().precise_done || '').replace(':charged', fmtTokens(item.preciseCharged || 0)));
      box.hidden = false;
      return;
    }
    const info = await preciseFetchInfo(force);
    if (state.activeResult !== item) return;
    if (!info || !info.enabled) { box.hidden = true; return; }
    const low = (r.confidence || 0) < 60;
    box.classList.toggle('low', low);
    if (lowEl) lowEl.hidden = !low;
    const per = info.per_page | 0, need = per * pages;
    $('[data-precise-desc]').textContent = (T().precise_desc || '').replace(':price', fmtTokens(per));
    const enough = !!info.logged_in && (info.balance | 0) >= need;
    go.hidden = !enough || state.precising;
    go.disabled = !!state.precising;
    go.textContent = (T().precise_go || 'Recognize more precisely') + ' · ' + fmtTokens(need);
    buy.hidden = enough || !info.store_url;
    if (info.store_url) buy.setAttribute('href', info.store_url);
    buy.setAttribute('data-tmb-short', String(info.logged_in ? Math.max(1, need - (info.balance | 0)) : need));
    if (!state.precising) {
      preciseState(enough ? '' : (info.logged_in
        ? (T().precise_balance_short || '').replace(':balance', fmtTokens(info.balance | 0)).replace(':short', fmtTokens(need - (info.balance | 0)))
        : (T().precise_guest || '')));
    }
    box.hidden = false;
    if (!item.preciseOffered) {
      item.preciseOffered = true;
      ae('feature_use', { slug: LIMIT_SLUG, action: 'ocr_precise_offer', pages, low: low ? 1 : 0, enough: enough ? 1 : 0, guest: info.logged_in ? 0 : 1, conf: Math.round(r.confidence || 0) });
    }
  }

  function newRid() {
    try { if (window.crypto && crypto.randomUUID) return crypto.randomUUID().replace(/-/g, ''); } catch (_) {}
    return Date.now().toString(36) + Math.random().toString(36).slice(2, 12);
  }

  // The page image the workspace shows (word boxes live in its pixels), no
  // longer than the lane accepts.
  async function pageBlob(pg) {
    const img = await loadImage(pg.imageDataUrl);
    const edge = (PRECISE && PRECISE.edge) || 3072;
    const k = Math.min(1, edge / Math.max(img.naturalWidth || img.width, img.naturalHeight || img.height));
    const c = document.createElement('canvas');
    c.width = Math.max(1, Math.round((img.naturalWidth || img.width) * k));
    c.height = Math.max(1, Math.round((img.naturalHeight || img.height) * k));
    c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
    const blob = await window.__canvasBlob(c, 'image/jpeg', 0.9);
    const out = { blob, w: c.width, h: c.height };
    c.width = c.height = 1;
    return out;
  }

  function csrfToken() {
    const m = document.querySelector('meta[name="csrf-token"]');
    return m ? m.getAttribute('content') : '';
  }

  // The order by its key: the answer to a page whose response never arrived.
  async function preciseCollect(rid) {
    const url = PRECISE.order.split('RIDPLACEHOLDER').join(encodeURIComponent(rid));
    const until = Date.now() + 5 * 60 * 1000;
    while (Date.now() < until) {
      try {
        const r = await fetch(url, { credentials: 'same-origin', headers: { 'Accept': 'application/json' } });
        let data = null; try { data = await r.json(); } catch (_) {}
        if (r.status === 200 && data && data.success) return { ok: true, status: 200, data };
        if (r.status === 404 || r.status === 401) return { ok: false, status: r.status, data };
      } catch (_) {}
      await new Promise(res => setTimeout(res, 3000));
    }
    return { ok: false, status: 0, data: null };
  }

  async function precisePage(pg, rid, langs) {
    const shot = await pageBlob(pg);
    if (!shot.blob) return { ok: false, status: 0, data: null };
    const fd = new FormData();
    fd.append('image', shot.blob, 'page.jpg');
    fd.append('langs', langs.join('+'));
    fd.append('rid', rid);
    let res;
    try {
      res = await fetch(PRECISE.post, { method: 'POST', body: fd, credentials: 'same-origin', headers: { 'Accept': 'application/json', 'X-CSRF-TOKEN': csrfToken() } });
    } catch (_) {
      const got = await preciseCollect(rid);
      return Object.assign(got, { w: shot.w, h: shot.h });
    }
    let data = null; try { data = await res.json(); } catch (_) {}
    if (res.status === 202 || (data === null && res.status >= 500)) {
      const got = await preciseCollect(rid);
      return Object.assign(got, { w: shot.w, h: shot.h });
    }
    return { ok: !!(res.ok && data && data.success), status: res.status, data, w: shot.w, h: shot.h };
  }

  function applyPrecise(pg, data, w, h) {
    const sx = (pg.imageWidth || w) / (data.width || w || 1);
    const sy = (pg.imageHeight || h) / (data.height || h || 1);
    const words = [];
    (data.lines || []).forEach(line => (line.words || []).forEach(wd => {
      const b = wd.bbox || {};
      // The precise lane gives no per-word confidence; the page is marked precise instead.
      words.push({ text: wd.text, confidence: 90, bbox: { x0: b.x0 * sx, y0: b.y0 * sy, x1: b.x1 * sx, y1: b.y1 * sy } });
    }));
    pg.text = formatText({ text: (data.lines || []).map(l => l.text).join('\n'), words }, state.mode);
    pg.words = words;
    pg.confidence = 90;
    pg.precise = true;
  }

  async function runPrecise() {
    const item = state.activeResult;
    if (!PRECISE || !item || !item.result || state.isProcessing || state.precising || item.preciseDone) return;
    const r = item.result;
    const pages = r.pages || [];
    if (!pages.length) return;
    state.precising = true;
    const go = $('[data-precise-go]');
    if (go) go.disabled = true;
    const langs = (r.language || resolveLangs().join('+')).split('+');
    const t0 = performance.now();
    item.preciseRids = item.preciseRids || [];
    let charged = 0, failed = null, provider = '';
    ae('feature_use', { slug: LIMIT_SLUG, action: 'ocr_precise_click', pages: pages.length });
    try {
      for (let i = 0; i < pages.length; i++) {
        const pg = pages[i];
        if (pg.precise) continue;
        preciseState((T().precise_working || '').replace(':n', i + 1).replace(':total', pages.length));
        const rid = item.preciseRids[i] || (item.preciseRids[i] = newRid());
        let res;
        try { res = await precisePage(pg, rid, langs); } catch (e) { res = { ok: false, status: 0, data: null }; handleError(e, 'ocr.precise'); }
        if (!res.ok) { failed = Object.assign({ page: i + 1 }, res); break; }
        applyPrecise(pg, res.data, res.w, res.h);
        charged += res.data.charged | 0;
        // Which rung of the server ladder read the page (deepinfra / claude / yandex) —
        // the share of the costlier fallbacks is read from these events.
        if (res.data.provider && provider.indexOf(res.data.provider) < 0) provider += (provider ? '+' : '') + res.data.provider;
        if (typeof res.data.balance === 'number' && preciseInfo) preciseInfo.balance = res.data.balance;
      }
    } finally {
      state.precising = false;
    }
    item.preciseCharged = (item.preciseCharged || 0) + charged;
    const doneAll = pages.every(p => p.precise);
    if (doneAll) {
      r.text = pages.map(p => p.text).join('\n\n');
      r.words = [].concat.apply([], pages.map(p => p.words));
      r.confidence = 90;
      r.engine = 'precise';
      r.provider = provider || 'precise';
      item.preciseDone = true;
      ae('feature_use', { slug: LIMIT_SLUG, action: 'ocr_precise_done', pages: pages.length, charged: item.preciseCharged, ms: Math.round(performance.now() - t0), provider: provider });
    }
    if (charged > 0 || doneAll) {
      showResult(item);
      saveSession();
      try { if (window.__aeToolComplete) window.__aeToolComplete({ action: 'ocr_precise', pages: pages.length }); } catch (_) {}
    }
    if (failed) {
      ae('feature_use', { slug: LIMIT_SLUG, action: 'ocr_precise_failed', status: failed.status | 0, page: failed.page });
      if (failed.status === 402) {
        // Not enough tokens (or a guest): the block turns into the buy step.
        await renderPrecise(item, true);
        return;
      }
      const msg = failed.data && failed.data.empty ? T().precise_empty
        : failed.status === 503 ? T().precise_off
        : (failed.data && typeof failed.data.error === 'string' && failed.data.error && failed.data.error !== 'insufficient_tokens') ? failed.data.error
        : T().precise_failed;
      await renderPrecise(item, true);
      preciseState(msg, true);
    }
  }

  function bindPrecise() {
    if (!PRECISE) return;
    const go = $('[data-precise-go]');
    if (go) go.addEventListener('click', runPrecise);
    // Tokens bought in the in-tool cashier (or another tab): the offer re-reads
    // the balance when the page comes back into view.
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden && state.activeResult && !state.precising) renderPrecise(state.activeResult, true);
    });
  }

  function stat(text) {
    const el = document.createElement('span');
    el.innerHTML = '<b>' + text + '</b>';
    return el;
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, c => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    })[c]);
  }

  function renderWordsHtml(words, fallbackText) {
    if (!words || !words.length) {
      const div = document.createElement('div');
      div.textContent = fallbackText || '';
      return div.innerHTML;
    }
    const parts = [];
    words.forEach((w, idx) => {
      const cls = confClass(w.confidence);
      const safe = (w.text || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      parts.push(`<span class="ocr-w ${cls}" data-word-id="${idx}">${safe}</span>`);
    });
    return parts.join(' ');
  }

  function confClass(c) {
    if (c >= 85) return 'high';
    if (c >= 70) return 'medium';
    if (c >= 50) return 'lowconf';
    return 'poor';
  }

  function renderBboxes(page) {
    const layer = $('[data-ws-bbox]');
    const wrap = $('[data-ws-image-wrap]');
    const img = $('[data-ws-img]');
    if (!layer || !img || !page) return;
    layer.innerHTML = '';
    const scaleX = img.clientWidth / page.imageWidth;
    const scaleY = img.clientHeight / page.imageHeight;
    const showAll = $('[data-bbox-toggle]').checked;
    const lowOnly = $('[data-bbox-low-only]').checked;
    if (!showAll) return;
    page.words.forEach((w, idx) => {
      if (!w.bbox) return;
      if (lowOnly && w.confidence >= 70) return;
      const box = document.createElement('div');
      box.className = 'ocr-ws-bbox-layer ' + confClass(w.confidence);
      box.style.left = (w.bbox.x0 * scaleX) + 'px';
      box.style.top = (w.bbox.y0 * scaleY) + 'px';
      box.style.width = ((w.bbox.x1 - w.bbox.x0) * scaleX) + 'px';
      box.style.height = ((w.bbox.y1 - w.bbox.y0) * scaleY) + 'px';
      box.dataset.wordId = idx;
      box.title = w.text + ' (' + Math.round(w.confidence) + '%)';
      box.addEventListener('click', () => focusWord(idx));
      layer.appendChild(box);
    });
  }

  function focusWord(idx) {
    const word = $('[data-text] [data-word-id="' + idx + '"]');
    if (!word) return;
    $$('[data-text] .focused').forEach(el => el.classList.remove('focused'));
    word.classList.add('focused');
    word.scrollIntoView({ block: 'nearest' });
    const box = $('[data-ws-bbox] [data-word-id="' + idx + '"]');
    $$('[data-ws-bbox] .focused').forEach(el => el.classList.remove('focused'));
    if (box) box.classList.add('focused');
  }

  function bindBbox() {
    const toggle = $('[data-bbox-toggle]');
    const lowOnly = $('[data-bbox-low-only]');
    [toggle, lowOnly].forEach(cb => cb && cb.addEventListener('change', () => {
      if (state.activeResult) renderBboxes(state.activeResult.result.pages[0]);
    }));
    window.addEventListener('resize', () => {
      if (state.activeResult) renderBboxes(state.activeResult.result.pages[0]);
    });
  }

  function bindText() {
    const textEl = $('[data-text]');
    if (!textEl) return;
    // Click a word → highlight corresponding bbox
    textEl.addEventListener('click', e => {
      const w = e.target.closest('[data-word-id]');
      if (!w) return;
      const id = parseInt(w.dataset.wordId);
      focusWord(id);
    });
    // Sync edits back to words array for export
    textEl.addEventListener('input', () => {
      if (!state.activeResult) return;
      // Collect word spans and update words in-place
      const spans = textEl.querySelectorAll('[data-word-id]');
      const page = state.activeResult.result.pages[0];
      spans.forEach(s => {
        const id = parseInt(s.dataset.wordId);
        if (page.words[id]) page.words[id].text = s.textContent;
      });
      state.activeResult.result.text = state.activeResult.result.pages.map(p => p.words.map(w => w.text).join(' ')).join('\n\n');
    });

    const copyBtn = $('[data-editor-copy]');
    if (copyBtn) copyBtn.addEventListener('click', async () => {
      try {
        await navigator.clipboard.writeText(textEl.innerText);
        toast(T().editor_copy_done, 'success');
        // Copying the recognised text IS the delivery, same as an export.
        funnel('export', { fmt: 'copy' });
        try { if (window.__aeToolComplete) window.__aeToolComplete({ action: 'ocr_copy' }); } catch (_) {}
      } catch (e) { handleError(e, 'copy'); }
    });

    const lowBtn = $('[data-lowconf-next]');
    if (lowBtn) lowBtn.addEventListener('click', () => {
      const lows = $$('[data-text] .lowconf, [data-text] .poor');
      if (!lows.length) return;
      const currentIdx = lows.findIndex(el => el.classList.contains('focused'));
      const next = lows[(currentIdx + 1) % lows.length];
      $$('[data-text] .focused').forEach(el => el.classList.remove('focused'));
      next.classList.add('focused');
      next.scrollIntoView({ block: 'nearest' });
      const id = parseInt(next.dataset.wordId);
      focusWord(id);
    });

    const qrOpen = $('[data-qr-open]');
    const qrCopy = $('[data-qr-copy]');
    if (qrOpen) qrOpen.addEventListener('click', () => {
      const code = state.activeResult && state.activeResult.qrCode;
      if (!code) return;
      if (/^https?:\/\//i.test(code)) window.open(code, '_blank', 'noopener');
    });
    if (qrCopy) qrCopy.addEventListener('click', async () => {
      const code = state.activeResult && state.activeResult.qrCode;
      if (!code) return;
      try { await navigator.clipboard.writeText(code); toast(T().editor_copy_done, 'success'); } catch (e) {}
    });
  }

  function bindSearch() {
    const input = $('[data-search]');
    const count = $('[data-search-count]');
    const prevBtn = $('[data-search-prev]');
    const nextBtn = $('[data-search-next]');
    let matches = []; let activeIdx = -1;
    const textEl = $('[data-text]');

    function run() {
      if (!textEl) return;
      const q = (input.value || '').trim().toLowerCase();
      $$('[data-text] mark').forEach(m => {
        const parent = m.parentNode;
        while (m.firstChild) parent.insertBefore(m.firstChild, m);
        parent.removeChild(m);
      });
      matches = []; activeIdx = -1;
      if (!q) { count.textContent = ''; return; }
      const walker = document.createTreeWalker(textEl, NodeFilter.SHOW_TEXT);
      const nodes = []; while (walker.nextNode()) nodes.push(walker.currentNode);
      nodes.forEach(node => {
        const lower = node.nodeValue.toLowerCase();
        let idx = lower.indexOf(q);
        if (idx < 0) return;
        const frag = document.createDocumentFragment();
        let last = 0;
        while (idx >= 0) {
          if (idx > last) frag.appendChild(document.createTextNode(node.nodeValue.slice(last, idx)));
          const mk = document.createElement('mark');
          mk.textContent = node.nodeValue.slice(idx, idx + q.length);
          frag.appendChild(mk);
          matches.push(mk);
          last = idx + q.length;
          idx = lower.indexOf(q, last);
        }
        if (last < node.nodeValue.length) frag.appendChild(document.createTextNode(node.nodeValue.slice(last)));
        node.parentNode.replaceChild(frag, node);
      });
      count.textContent = matches.length ? plural(T().search_match_count, matches.length) : T().search_no_matches;
      if (matches.length) step(1);
    }
    function step(dir) {
      if (!matches.length) return;
      matches.forEach(m => m.classList.remove('active'));
      activeIdx = (activeIdx + dir + matches.length) % matches.length;
      const m = matches[activeIdx];
      m.classList.add('active');
      m.scrollIntoView({ block: 'nearest' });
    }
    if (input) input.addEventListener('input', run);
    if (prevBtn) prevBtn.addEventListener('click', () => step(-1));
    if (nextBtn) nextBtn.addEventListener('click', () => step(1));
    if (input) input.addEventListener('keydown', e => {
      if (e.key === 'Enter') { e.preventDefault(); step(e.shiftKey ? -1 : 1); }
    });
  }

  // ─── Export ────────────────────────────────────────────────────────────
  function bindExport() {
    $$('[data-export-fmt]').forEach(btn => {
      btn.addEventListener('click', async () => {
        const fmt = btn.dataset.exportFmt;
        const exportStart = performance.now();
        try {
          await runExport(fmt);
          ae('ocr_export', {
            fmt,
            files: state.queue.filter(q => q.status === 'done').length,
            ms: Math.round(performance.now() - exportStart),
          });
          funnel('export', { fmt });
          try { if (window.__aeToolComplete) window.__aeToolComplete({ action: 'ocr_export', fmt }); } catch (e) {}
        } catch (err) {
          handleError(err, 'export');
          ae('tool_error', {
            slug: 'ocr-text-extractor',
            error_type: 'export_' + fmt,
            msg: (err.message || '').slice(0, 200),
          });
          toast(T().err_export, 'error');
        }
      });
    });
  }

  async function runExport(fmt) {
    const results = state.queue.filter(q => q.status === 'done' && q.result).map(q => {
      const combined = {
        fileName: q.result.fileName,
        text: q.result.text,
        words: q.result.words,
        confidence: q.result.confidence,
        language: q.result.language,
        // Single image path for searchable PDF: use first page only
        imageDataUrl: q.result.pages[0] && q.result.pages[0].imageDataUrl,
        imageWidth: q.result.pages[0] && q.result.pages[0].imageWidth,
        imageHeight: q.result.pages[0] && q.result.pages[0].imageHeight,
      };
      // For multi-page PDFs we export per-page images
      combined.pages = q.result.pages;
      return combined;
    });
    if (!results.length) return;
    const baseName = results.length === 1 ? results[0].fileName.replace(/\.\w+$/, '') : 'ocr-batch';

    setProgressTitle((T().export_working || 'Preparing…').replace('{format}', fmt.toUpperCase()));

    if (fmt === 'txt') {
      window.OcrExport.download(window.OcrExport.txt(results), window.OcrExport.safeName(baseName, 'txt'));
    } else if (fmt === 'md') {
      window.OcrExport.download(window.OcrExport.markdown(results), window.OcrExport.safeName(baseName, 'md'));
    } else if (fmt === 'json') {
      window.OcrExport.download(window.OcrExport.toJson(results), window.OcrExport.safeName(baseName, 'json'));
    } else if (fmt === 'docx') {
      const blob = await window.OcrExport.docx(results, { mode: state.mode });
      window.OcrExport.download(blob, window.OcrExport.safeName(baseName, 'docx'));
    } else if (fmt === 'xlsx') {
      const blob = window.OcrExport.xlsx(results);
      window.OcrExport.download(blob, window.OcrExport.safeName(baseName, 'xlsx'));
    } else if (fmt === 'pdf') {
      // Flatten multi-page results into per-page entries for the searchable PDF
      const pageEntries = [];
      results.forEach(r => r.pages.forEach(p => pageEntries.push({
        fileName: r.fileName,
        imageDataUrl: p.imageDataUrl,
        imageWidth: p.imageWidth,
        imageHeight: p.imageHeight,
        words: p.words,
        text: p.text,
      })));
      const blob = await window.OcrExport.searchablePdf(pageEntries);
      window.OcrExport.download(blob, window.OcrExport.safeName(baseName, 'pdf'));
    } else if (fmt === 'zip') {
      const named = [];
      named.push({ name: 'text.txt', blob: window.OcrExport.txt(results) });
      try { named.push({ name: 'document.docx', blob: await window.OcrExport.docx(results, { mode: state.mode }) }); } catch (e) {}
      try { named.push({ name: 'tables.xlsx', blob: window.OcrExport.xlsx(results) }); } catch (e) {}
      named.push({ name: 'data.json', blob: window.OcrExport.toJson(results) });
      const zipped = await window.OcrExport.zipAll(named);
      window.OcrExport.download(zipped, window.OcrExport.safeName(baseName, 'zip'));
    } else if (fmt === 'copy-all') {
      const text = results.map(r => r.text).join('\n\n');
      try { await navigator.clipboard.writeText(text); toast(T().editor_copy_done, 'success'); }
      catch (e) { handleError(e, 'copy-all'); }
    } else if (fmt === 'print') {
      runPrint();
    } else if (fmt === 'translate') {
      openTranslator();
    } else if (fmt === 'listen') {
      openInDocumentReader(results, baseName);
    }
  }

  // ── "Listen" handoff → /document-reader ─────────────────────────────────
  // Closes the loop the 2026-07-02 bug report exposed: scanned book → OCR →
  // listen. The recognized text travels via sessionStorage (same pattern as
  // auto-captions → video-subtitles); the reader's boot-check turns it into a
  // .txt File and injects it through the SHARED uploader — the whole standard
  // load path (validation, telemetry, bookmarks keying) comes for free.
  function openInDocumentReader(results, baseName) {
    try {
      const text = results.map(r => r.text || '').join('\n\n').trim();
      if (!text) { toast(T().err_empty_result || 'No text to listen to.', 'warning'); return; }
      // sessionStorage practical budget ~5 MB; recognized text of a 150-page
      // range is ~300 KB — cap defensively at 2 M chars.
      const payload = { v: 1, name: (baseName || 'ocr') + '.txt', text: text.slice(0, 2000000), ts: Date.now() };
      sessionStorage.setItem('__handoff_ocr_to_dr_v1', JSON.stringify(payload));
      ae('feature_use', { slug: 'ocr-text-extractor', action: 'listen_handoff', chars: payload.text.length });
      const locale = window.__LOCALE || 'en';
      // NB: no 'noopener' — sessionStorage only COPIES into auxiliary browsing
      // contexts; noopener makes the tab non-auxiliary and the handoff key
      // would never arrive. Same-origin, so the opener link is harmless.
      window.open('/' + locale + '/document-reader', '_blank');
    } catch (e) {
      handleError(e, 'listen-handoff');
      toast(T().err_export || 'Could not open the reader.', 'error');
    }
  }

  // ─── Print (browser native) ────────────────────────────────────────────
  function bindPrint() {
    const btn = $('[data-export-fmt="print"]');
    if (btn) btn.addEventListener('click', runPrint);
  }
  function runPrint() {
    const results = state.queue.filter(q => q.status === 'done' && q.result);
    if (!results.length) return;
    ae('feature_use', { slug: 'ocr-text-extractor', action: 'print_clicked' });
    const text = results.map(r => (r.result.fileName + '\n\n' + (r.result.text || ''))).join('\n\n────────\n\n');
    const safeText = text.replace(/[&<>]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' })[c]);
    // Инлайн-скрипта в разметке быть НЕ должно — попап наследует наш CSP и
    // режет parser-inserted скрипт без нонса (см. __printWindow).
    window.__printWindow(
      '<!doctype html><html><head><meta charset="utf-8"><title>' +
      (T().print_title || 'OCR result') + '</title>' +
      '<style>body{font:14px/1.55 -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;color:#000;background:#fff;padding:24px;max-width:780px;margin:0 auto}h1{font-size:18px;margin:0 0 16px}pre{white-space:pre-wrap;word-wrap:break-word;font-family:inherit;font-size:14px;margin:0}@media print{body{padding:0}}</style>' +
      '</head><body><h1>' + (T().print_title || 'OCR result') + '</h1><pre>' + safeText + '</pre>' +
      '</body></html>',
      { features: 'width=720,height=900',
        onBlocked: () => toast('Popup blocked — please allow popups', 'warning') }
    );
  }

  // ─── Translate-after — opens a new tab with extracted text pre-filled ──
  function openTranslator() {
    const results = state.queue.filter(q => q.status === 'done' && q.result);
    if (!results.length) return;
    const text = results.map(r => r.result.text).join('\n\n').slice(0, 4000);
    ae('feature_use', { slug: 'ocr-text-extractor', action: 'translate_clicked', chars: text.length });
    // Use Google Translate as universal target — no custom translator tool
    // exists in Timbrica yet; sl=auto detects source automatically.
    const url = 'https://translate.google.com/?sl=auto&tl=' + encodeURIComponent(window.__OCR_LOCALE || 'en') +
      '&op=translate&text=' + encodeURIComponent(text);
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  // ─── Read-aloud (Web Speech API) ───────────────────────────────────────
  let ttsUtter = null;
  function bindReadAloud() {
    const btn = $('[data-tts-toggle]');
    if (!btn) return;
    btn.addEventListener('click', () => {
      if (!('speechSynthesis' in window)) return toast('Read-aloud not supported in this browser', 'warning');
      const synth = window.speechSynthesis;
      if (synth.speaking || synth.paused) {
        synth.cancel(); ttsUtter = null;
        btn.dataset.state = 'idle';
        return;
      }
      const txt = $('[data-text]') ? $('[data-text]').innerText : '';
      if (!txt.trim()) return;
      const u = new SpeechSynthesisUtterance(txt.slice(0, 32000));
      const lang3 = (state.activeResult && state.activeResult.language) || 'eng';
      u.lang = TESS_TO_BCP47[lang3.split('+')[0]] || 'en-US';
      u.onend = () => { btn.dataset.state = 'idle'; };
      u.onerror = () => { btn.dataset.state = 'idle'; };
      ttsUtter = u;
      btn.dataset.state = 'speaking';
      ae('feature_use', { slug: 'ocr-text-extractor', action: 'read_aloud_started', lang: u.lang });
      synth.speak(u);
    });
  }
  // Tesseract 3-letter → BCP47 (subset that matters; falls back to en-US)
  const TESS_TO_BCP47 = {
    eng: 'en-US', rus: 'ru-RU', spa: 'es-ES', fra: 'fr-FR', deu: 'de-DE',
    ita: 'it-IT', por: 'pt-PT', nld: 'nl-NL', pol: 'pl-PL', tur: 'tr-TR',
    chi_sim: 'zh-CN', chi_tra: 'zh-TW', jpn: 'ja-JP', kor: 'ko-KR',
    ara: 'ara', heb: 'he-IL', hin: 'hi-IN', tha: 'th-TH', vie: 'vi-VN',
    ind: 'id-ID', ukr: 'uk-UA',
  };

  // ─── Find & Replace in editor ──────────────────────────────────────────
  function bindFindReplace() {
    const tgl = $('[data-fr-toggle]');
    const panel = $('[data-fr-panel]');
    const findInp = $('[data-fr-find]');
    const replaceInp = $('[data-fr-replace]');
    const btnOne = $('[data-fr-replace-one]');
    const btnAll = $('[data-fr-replace-all]');
    const counter = $('[data-fr-count]');
    if (!tgl || !panel) return;

    tgl.addEventListener('click', () => panel.classList.toggle('active'));

    function getText() { const e = $('[data-text]'); return e ? e.innerText : ''; }
    function setText(t) { const e = $('[data-text]'); if (e) e.innerText = t; if (state.activeResult) state.activeResult.text = t; saveSession(); }

    function count() {
      const q = (findInp.value || '');
      if (!q) { counter.textContent = ''; return 0; }
      const re = new RegExp(q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
      const n = (getText().match(re) || []).length;
      counter.textContent = (T().fr_count || '{n} match|{n} matches').split('|')[n === 1 ? 0 : 1].replace('{n}', n);
      return n;
    }

    findInp.addEventListener('input', count);

    btnOne.addEventListener('click', () => {
      const q = findInp.value, r = replaceInp.value || '';
      if (!q) return;
      const txt = getText();
      const idx = txt.indexOf(q);
      if (idx < 0) return;
      setText(txt.slice(0, idx) + r + txt.slice(idx + q.length));
      ae('feature_use', { slug: 'ocr-text-extractor', action: 'replace_one' });
      count();
    });
    btnAll.addEventListener('click', () => {
      const q = findInp.value, r = replaceInp.value || '';
      if (!q) return;
      const re = new RegExp(q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
      const txt = getText();
      const next = txt.replace(re, r);
      const replaced = (txt.match(re) || []).length;
      setText(next);
      ae('feature_use', { slug: 'ocr-text-extractor', action: 'replace_all', n: replaced });
      count();
      toast((T().fr_replaced || 'Replaced {n}').replace('{n}', replaced), 'success');
    });
  }

  // ─── Shortcuts modal ───────────────────────────────────────────────────
  function bindShortcutsModal() {
    const open = $('[data-shortcuts-open]');
    const modal = $('[data-shortcuts-modal]');
    if (!open || !modal) return;
    open.addEventListener('click', () => {
      modal.classList.add('active');
      ae('feature_use', { slug: 'ocr-text-extractor', action: 'shortcuts_opened' });
    });
    modal.addEventListener('click', e => {
      if (e.target === modal || e.target.matches('[data-shortcuts-close]')) modal.classList.remove('active');
    });
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && modal.classList.contains('active')) modal.classList.remove('active');
    });
  }

  // ─── Workspace image zoom ──────────────────────────────────────────────
  function bindWorkspaceZoom() {
    const wrap = $('[data-ws-image-wrap]');
    if (!wrap) return;
    let zoom = 1;
    const apply = () => { wrap.style.transform = 'scale(' + zoom + ')'; wrap.style.transformOrigin = '0 0'; };
    const panel = $('[data-ws-image]');
    if (!panel) return;
    panel.addEventListener('wheel', e => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      const next = Math.min(4, Math.max(0.5, zoom + (e.deltaY < 0 ? 0.15 : -0.15)));
      if (next === zoom) return;
      zoom = next; apply();
      ae('feature_use', { slug: 'ocr-text-extractor', action: 'zoom_changed', level: zoom.toFixed(2) });
    }, { passive: false });
    // Double-click resets
    panel.addEventListener('dblclick', () => { zoom = 1; apply(); });
  }

  // ─── Queue drag-to-reorder ─────────────────────────────────────────────
  function bindQueueDragReorder() {
    const list = $('[data-queue-list]');
    if (!list) return;
    let dragId = null;
    list.addEventListener('dragstart', e => {
      const item = e.target.closest('.ocr-queue-item');
      if (!item) return;
      dragId = item.dataset.fileId;
      e.dataTransfer.effectAllowed = 'move';
      item.classList.add('dragging');
    });
    list.addEventListener('dragover', e => {
      const target = e.target.closest('.ocr-queue-item');
      if (!target || !dragId || target.dataset.fileId === dragId) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
    });
    list.addEventListener('drop', e => {
      const target = e.target.closest('.ocr-queue-item');
      if (!target || !dragId) return;
      e.preventDefault();
      const targetId = target.dataset.fileId;
      const from = state.queue.findIndex(q => q.id === dragId);
      const to = state.queue.findIndex(q => q.id === targetId);
      if (from < 0 || to < 0) return;
      const [moved] = state.queue.splice(from, 1);
      state.queue.splice(to, 0, moved);
      renderQueue();
      saveSession();
      ae('feature_use', { slug: 'ocr-text-extractor', action: 'queue_reordered' });
    });
    list.addEventListener('dragend', () => {
      list.querySelectorAll('.ocr-queue-item').forEach(it => it.classList.remove('dragging'));
      dragId = null;
    });
  }

  // applyPreprocessing is exposed so the OOM-degradation path can be tested
  // without a low-RAM phone (tests/playwright/ocr-preprocess-oom.mjs).
  window.OcrApp = { init, state, _applyPreprocessing: applyPreprocessing };
})();
